<template>
  <div class="kanban-page">
    <!-- Indicador de reconexão WebSocket -->
    <transition name="ws-fade">
      <div v-if="!wsConnected" class="ws-reconnecting-bar">
        <div class="ws-reconnecting-content">
          <svg class="ws-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
          <span>{{ t('kanban.page.reconnecting') }}</span>
        </div>
      </div>
    </transition>
    <!-- Header Minimalista Moderno -->
    <div class="clean-header">
      <div class="header-content">
        <!-- Título e Badge -->
        <div class="header-left">
          <button class="mobile-header-submenu-btn" @click="toggleWorkspaceSubmenuMobile" type="button" :aria-label="t('kanban.page.openMenu')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.4" d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <h1 class="workspace-title">{{ workspace?.nome || t('kanban.page.defaultTitle') }}</h1>
          <div class="workspace-type-badge" :class="workspace?.tipo?.toLowerCase()">
            {{ workspace?.tipo === 'TAREFAS' ? t('kanban.page.workspaceType.tasks') : t('kanban.page.workspaceType.crm') }}
          </div>
        </div>
        
        <!-- Controles Centrais -->
        <div class="header-center">
          <!-- Botão para abrir popover de toggles + filtro de busca -->
          <div class="view-toggles-wrapper">
            <button
              v-if="canUseKanbanPreviewMenu()"
              @click="showTogglesPopover = !showTogglesPopover"
              class="toggle-popover-btn"
              :class="{ 'active': showTogglesPopover }"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
              </svg>
              <span class="toggle-popover-label">{{ t('kanban.page.view') }}</span>
            </button>

            <!-- Filtro de texto para cards/leads -->
            <div class="kanban-search-input-wrapper">
              <input
                v-model="searchText"
                type="text"
                class="kanban-search-input"
                :placeholder="workspace?.tipo === 'TAREFAS' ? t('kanban.page.filter.tasks') : t('kanban.page.filter.leads')"
              />
            </div>
            
            <!-- Popover com toggles -->
            <div 
              v-if="showTogglesPopover" 
              class="toggles-popover"
              @click.stop
            >
              <div class="popover-header">
                <h3 class="popover-title">{{ t('kanban.page.viewOptionsTitle') }}</h3>
              </div>
              <div class="popover-content">
                <!-- Toggle Mostrar Descrição (apenas para TAREFAS) -->
                <div 
                  v-if="workspace?.tipo === 'TAREFAS' && canShowPreviewToggle('showDescription')"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.showDescription.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.showDescription.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleDescription"
                    class="toggle-switch"
                    :class="{ 'active': showDescription }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Expandir Tags -->
                <div v-if="canShowPreviewToggle('showTagsExpanded')" class="toggle-item">
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.expandTags.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.expandTags.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleTagsExpanded"
                    class="toggle-switch"
                    :class="{ 'active': showTagsExpanded }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Mostrar Usuários -->
                <div v-if="canShowPreviewToggle('showUsers')" class="toggle-item">
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.showUsers.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.showUsers.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleUsers"
                    class="toggle-switch"
                    :class="{ 'active': showUsers }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Controles de Atividade (apenas para TAREFAS) -->
                <div 
                  v-if="workspace?.tipo === 'TAREFAS' && canShowPreviewToggle('showActivityControls')"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.activityControls.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.activityControls.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleActivityControls"
                    class="toggle-switch"
                    :class="{ 'active': showActivityControls }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Agendamento -->
                <div v-if="canShowPreviewToggle('showSchedule')" class="toggle-item">
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.schedule.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.schedule.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleSchedule"
                    class="toggle-switch"
                    :class="{ 'active': showSchedule }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Checklist (apenas para TAREFAS) -->
                <div 
                  v-if="workspace?.tipo === 'TAREFAS' && canShowPreviewToggle('showChecklist')"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.checklist.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.checklist.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleChecklist"
                    class="toggle-switch"
                    :class="{ 'active': showChecklist }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Anexos -->
                <div v-if="canShowPreviewToggle('showAttachments')" class="toggle-item">
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.attachments.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.attachments.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleAttachments"
                    class="toggle-switch"
                    :class="{ 'active': showAttachments }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>

                <!-- Datas de criação e última alteração -->
                <div v-if="canShowPreviewToggle('showCreatedAt')" class="toggle-item">
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.createdAt.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.createdAt.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleShowCreatedAt"
                    class="toggle-switch"
                    :class="{ 'active': showCreatedAt }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>

                <div v-if="canShowPreviewToggle('showUpdatedAt')" class="toggle-item">
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.updatedAt.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.updatedAt.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleShowUpdatedAt"
                    class="toggle-switch"
                    :class="{ 'active': showUpdatedAt }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Detalhes do Contato (apenas para CRM) -->
                <div 
                  v-if="workspace?.tipo === 'CRM'"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.contactDetails.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.contactDetails.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleContactDetails"
                    class="toggle-switch"
                    :class="{ 'active': showContactDetails }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Informações do Negócio (apenas para CRM) -->
                <div 
                  v-if="workspace?.tipo === 'CRM'"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.businessInfo.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.businessInfo.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleBusinessInfo"
                    class="toggle-switch"
                    :class="{ 'active': showBusinessInfo }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>

                <!-- Alertas WhatsApp (apenas CRM) -->
                <div
                  v-if="workspace?.tipo === 'CRM'"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.whatsappNotify.sound.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.whatsappNotify.sound.desc') }}</span>
                  </div>
                  <button
                    type="button"
                    @click="toggleWhatsappNotifySound"
                    class="toggle-switch"
                    :class="{ 'active': whatsappNotifySound }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                <div
                  v-if="workspace?.tipo === 'CRM' && WHATSAPP_DESKTOP_NOTIFY_UI_ENABLED"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.whatsappNotify.desktop.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.whatsappNotify.desktop.desc') }}</span>
                    <span
                      class="toggle-item-permission-hint"
                      :class="`toggle-item-permission-hint--${notificationPermission}`"
                    >{{ notificationPermissionHint }}</span>
                  </div>
                  <button
                    type="button"
                    @click.stop="toggleWhatsappNotifyDesktop"
                    class="toggle-switch"
                    :class="{ 'active': whatsappNotifyDesktop }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
                
                <!-- Toggle Financeiro (apenas para TAREFAS) -->
                <div 
                  v-if="workspace?.tipo === 'TAREFAS' && canShowPreviewToggle('showFinanceiro')"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.finance.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.finance.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleFinanceiro"
                    class="toggle-switch"
                    :class="{ 'active': showFinanceiro }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>

                <div 
                  v-if="workspace?.tipo === 'TAREFAS' && canShowPreviewToggle('showTaskProgress')"
                  class="toggle-item"
                >
                  <div class="toggle-item-info">
                    <span class="toggle-item-label">{{ t('kanban.page.toggles.taskProgress.label') }}</span>
                    <span class="toggle-item-desc">{{ t('kanban.page.toggles.taskProgress.desc') }}</span>
                  </div>
                  <button 
                    @click="toggleTaskProgress"
                    class="toggle-switch"
                    :class="{ 'active': showTaskProgress }"
                  >
                    <span class="toggle-switch-slider"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- View toggles antigos (ocultos) -->
          <div class="view-toggles" style="display: none;">
            <!-- Toggle Mostrar Descrição (apenas para TAREFAS) -->
            <div class="tooltip-container">
              <button 
                v-if="workspace?.tipo === 'TAREFAS'"
                @click="toggleDescription"
                class="toggle-btn"
                :class="{ 'active': showDescription }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.description') }}</div>
            </div>
            
            <!-- Toggle Expandir Tags -->
            <div class="tooltip-container">
              <button 
                @click="toggleTagsExpanded"
                class="toggle-btn"
                :class="{ 'active': showTagsExpanded }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.tags') }}</div>
            </div>
            
            <!-- Toggle Mostrar Usuários -->
            <div class="tooltip-container">
              <button 
                @click="toggleUsers"
                class="toggle-btn"
                :class="{ 'active': showUsers }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.users') }}</div>
            </div>
            
            <!-- Toggle Controles de Atividade (apenas para TAREFAS) -->
            <div class="tooltip-container">
              <button 
                v-if="workspace?.tipo === 'TAREFAS'"
                @click="toggleActivityControls"
                class="toggle-btn"
                :class="{ 'active': showActivityControls }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.activities') }}</div>
            </div>
            
            <!-- Toggle Agendamento -->
            <div class="tooltip-container">
              <button 
                @click="toggleSchedule"
                class="toggle-btn"
                :class="{ 'active': showSchedule }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.schedule') }}</div>
            </div>
            
            <!-- Toggle Checklist (apenas para TAREFAS) -->
            <div class="tooltip-container">
              <button 
                v-if="workspace?.tipo === 'TAREFAS'"
                @click="toggleChecklist"
                class="toggle-btn"
                :class="{ 'active': showChecklist }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.checklist') }}</div>
            </div>
            
            <!-- Toggle Anexos -->
            <div class="tooltip-container">
              <button 
                @click="toggleAttachments"
                class="toggle-btn"
                :class="{ 'active': showAttachments }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.attachments') }}</div>
            </div>
            
            <!-- Toggle Detalhes do Contato (apenas para CRM) -->
            <div class="tooltip-container">
              <button 
                v-if="workspace?.tipo === 'CRM'"
                @click="toggleContactDetails"
                class="toggle-btn"
                :class="{ 'active': showContactDetails }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.contact') }}</div>
            </div>
            
            <!-- Toggle Informações do Negócio (apenas para CRM) -->
            <div class="tooltip-container">
              <button 
                v-if="workspace?.tipo === 'CRM'"
                @click="toggleBusinessInfo"
                class="toggle-btn"
                :class="{ 'active': showBusinessInfo }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.business') }}</div>
            </div>
            
            <!-- Toggle Is Financeiro (apenas para TAREFAS) -->
            <div class="tooltip-container">
              <button 
                v-if="workspace?.tipo === 'TAREFAS'"
                @click="toggleFinanceiro"
                class="toggle-btn"
                :class="{ 'active': showFinanceiro }"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
              <div class="tooltip">{{ t('kanban.page.tooltips.finance') }}</div>
            </div>
          </div>
        </div>
        
        <!-- Ações -->
        <div class="header-right">
          <button
            v-if="workspace?.tipo === 'CRM'"
            @click="abrirModalArquivados"
            class="action-btn secondary action-btn--icon-only"
            :title="t('kanban.page.actions.archivedLeads')"
            :aria-label="t('kanban.page.actions.archivedLeads')"
          >
            <div class="btn-icon">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
              </svg>
            </div>
          </button>

          <button 
            @click="showCreateColumnModal = true"
            class="action-btn secondary"
            v-if="effectivePermissions.canCreateColumns"
          >
            <div class="btn-icon">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
            </div>
            <span class="btn-text">{{ t('kanban.page.actions.newColumn') }}</span>
          </button>
          
          <button 
            @click="showCreateCardModal = true"
            class="action-btn primary"
            v-if="effectivePermissions.canMoveCards"
          >
            <div class="btn-icon">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
            </div>
            <span class="btn-text">{{ workspace?.tipo === 'TAREFAS' ? t('kanban.page.actions.newTask') : t('kanban.page.actions.newLead') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Board do Kanban -->
    <div class="kanban-board" ref="kanbanBoard">
          <draggable
            v-if="columns.length > 0"
            v-model="columns"
            class="kanban-columns"
            item-key="id"
            :animation="200"
            handle=".column-drag-handle"
            ghost-class="column-ghost"
            chosen-class="column-chosen"
            drag-class="column-dragging"
            :force-fallback="true"
            :move="onMoveColumn"
            @end="onColumnDragEnd"
          >
            <template #item="{ element: column }">
              <KanbanColumn
                :key="column.id"
                :column="column"
                :workspace-type="workspace?.tipo"
                :cards="getCardsByColumn(column.id)"
                :show-description="showDescription && effectivePermissions.showDescription"
                :show-tags-expanded="showTagsExpanded && effectivePermissions.showTagsExpanded"
                :show-users="showUsers && effectivePermissions.showUsers"
                :show-activity-controls="showActivityControls && effectivePermissions.showActivityControls"
                :show-schedule="showSchedule && effectivePermissions.showSchedule"
                :show-checklist="showChecklist && effectivePermissions.showChecklist"
                :show-attachments="showAttachments && effectivePermissions.showAttachments"
                :show-contact-details="showContactDetails"
                :show-business-info="showBusinessInfo"
                :show-financeiro="showFinanceiro && effectivePermissions.showFinanceiro"
                :show-task-progress="showTaskProgress && effectivePermissions.showTaskProgress"
                :show-created-at="showCreatedAt && effectivePermissions.showCreatedAt"
                :show-updated-at="showUpdatedAt && effectivePermissions.showUpdatedAt"
                :workspace-tags="workspaceTags"
                :workspace-users="workspaceUsers"
                :workspace-motivos="workspaceMotivos"
                :is-locked-by-permission="isColumnLocked(column.id)"
                :can-move-columns="effectivePermissions.canMoveColumns"
                :can-move-cards="effectivePermissions.canMoveCards"
                :can-create-cards-in-backlog="effectivePermissions.canMoveCards"
                :can-archive-cards="effectivePermissions.canArchiveCards"
                :can-manage-columns="effectivePermissions.canCreateColumns"
                :can-delete-columns="effectivePermissions.canDeleteColumns"
                :can-edit-lead="canEditCrmLead"
                @move-card="handleMoveCard"
                @reorder-card-local="handleReorderLocal"
                @update-card="handleUpdateCard"
                @delete-card="handleDeleteCard"
                @edit-column="handleEditColumn"
                @delete-column="handleDeleteColumn"
                @card-detail="handleCardDetail"
                @add-task="handleAddTask"
                @add-lead="handleAddLead"
              />
            </template>
          </draggable>
      
      <!-- Estado vazio -->
      <div v-else class="kanban-empty">
        <div class="empty-state">
          <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
          </svg>
          <h3 class="text-lg font-semibold text-gray-300 mb-2">{{ t('kanban.page.empty.noColumnsTitle') }}</h3>
          <p class="text-gray-400 mb-4">{{ workspace?.tipo === 'TAREFAS' ? t('kanban.page.empty.descriptionWork') : t('kanban.page.empty.descriptionSalesPipeline') }}</p>
          <button 
            @click="showCreateColumnModal = true"
            class="btn-primary"
          >
            {{ t('kanban.page.empty.createFirstColumn') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modais -->
    <CreateColumnModal
      v-if="showCreateColumnModal"
      :workspace-id="workspaceId"
      @close="showCreateColumnModal = false"
      @created="handleColumnCreated"
    />
    
    <EditColumnModal
      v-if="showEditColumnModal && selectedColumn"
      :column="selectedColumn"
      @close="showEditColumnModal = false"
      @updated="handleColumnUpdated"
    />
    
    <CreateTaskModal
      v-if="showCreateCardModal && workspace?.tipo === 'TAREFAS'"
      :workspace-id="workspaceId"
      :columns="columns"
      @close="showCreateCardModal = false"
      @created="handleCardCreated"
    />
    
    <CreateLeadModal
      v-if="showCreateCardModal && workspace?.tipo === 'CRM'"
      :workspace-id="workspaceId"
      :columns="columns"
      @close="showCreateCardModal = false"
      @created="handleCardCreated"
    />
    
    <!-- Modais Detalhados -->
    <TaskDetailModal 
      v-if="showTaskDetailModal"
      :is-open="showTaskDetailModal"
      :task="selectedTask"
      @close="showTaskDetailModal = false"
      @save="handleTaskSave"
      @taskUpdated="handleTaskUpdated"
    />
    
    <LeadDetailModal 
      v-if="showLeadDetailModal"
      :is-open="showLeadDetailModal"
      :lead="selectedLead"
      @close="showLeadDetailModal = false"
      @save="handleLeadSave"
      @lead-updated="handleLeadUpdated"
      @open-existing-lead="handleOpenExistingLead"
    />

    <ArchivedLeadsModal
      :open="showArchivedLeadsModal"
      :loading="loadingArquivados"
      :items="arquivadosLeads"
      :total="arquivadosTotal"
      :page="arquivadosPage"
      :page-size="ARCHIVED_LEADS_PAGE_SIZE"
      :total-pages="arquivadosTotalPages"
      :busy-id="arquivadosBusyId"
      :search="arquivadosSearch"
      @close="showArchivedLeadsModal = false"
      @search="onArquivadosSearch"
      @page-change="carregarArquivados"
      @open-lead="abrirLeadArquivado"
      @unarchive="desarquivarLeadKanban"
    />
    
    <SchedulerModal 
      v-if="showSchedulerModal"
      :is-open="showSchedulerModal"
      :initial-date="selectedDate"
      @close="showSchedulerModal = false"
      @confirm="handleAppointmentConfirm"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useWorkspaceStore } from '@/stores/workspace'
import KanbanColumn from '@/components/KanbanColumn.vue'
import CreateColumnModal from '@/components/CreateColumnModal.vue'
import EditColumnModal from '@/components/EditColumnModal.vue'
import CreateTaskModal from '@/components/CreateTaskModal.vue'
import CreateLeadModal from '@/components/CreateLeadModal.vue'
import TaskDetailModal from '@/components/TaskDetailModal.vue'
import LeadDetailModal from '@/components/LeadDetailModal.vue'
import ArchivedLeadsModal from '@/components/ArchivedLeadsModal.vue'
import SchedulerModal from '@/components/SchedulerModal.vue'
import draggable from 'vuedraggable'
import Swal from 'sweetalert2'
import api from '@/services/api'
import { buildKanbanWebSocketUrl, resolveMediaUrl } from '@/config/app'
import { usePlanoFeatures } from '@/composables/usePlanoFeatures'
import { KANBAN_PREVIEW_PLAN_KEYS } from '@/constants/modalPlanSections'
import { useAuthStore } from '@/stores/auth'
import {
  unlockAudioOnUserGesture,
  loadWhatsAppNotifyPrefs,
  saveWhatsAppNotifyPref,
  requestDesktopNotificationPermissionFromGesture,
  setWhatsAppNotificationClickHandler,
  showTestDesktopNotification,
  getNotificationPermission,
  formatNotificationPermissionHint,
  registerWhatsAppKanbanNotifyProvider,
  ensureWhatsAppNotifyListener,
  WHATSAPP_DESKTOP_NOTIFY_UI_ENABLED
} from '@/utils/whatsappIncomingNotify'

export default {
  name: 'KanbanPage',
  components: {
    KanbanColumn,
    CreateColumnModal,
    EditColumnModal,
    CreateTaskModal,
    CreateLeadModal,
    TaskDetailModal,
    LeadDetailModal,
    ArchivedLeadsModal,
    SchedulerModal,
    draggable
  },
  setup() {
    const { t } = useI18n()
    const route = useRoute()
    const router = useRouter()
    const workspaceStore = useWorkspaceStore()
    const planoFeatures = usePlanoFeatures()
    const authStore = useAuthStore()

    const toggleWorkspaceSubmenuMobile = () => {
      if (typeof window === 'undefined') return
      window.dispatchEvent(new CustomEvent('toggle-workspace-submenu'))
    }
    
    // Refs
    const workspaceId = ref(parseInt(route.params.id))
    // === Realtime WebSocket ===
    let ws = null
    let reloadLeadsTimer = null
    let reloadTasksTimer = null
    let wsReconnectTimer = null
    let wsHeartbeatTimer = null
    let wsPongTimer = null
    let wsReconnectAttempts = 0
    let wsIntentionalClose = false
    let wsHasConnectedBefore = false
    let wsConnectGeneration = 0
    const wsConnected = ref(true)

    const WS_HEARTBEAT_INTERVAL = 25000
    const WS_PONG_TIMEOUT = 10000
    const WS_INITIAL_RECONNECT_DELAY = 1000
    const WS_MAX_RECONNECT_DELAY = 30000

    const scheduleReloadLeads = () => {
      if (reloadLeadsTimer) clearTimeout(reloadLeadsTimer)
      reloadLeadsTimer = setTimeout(async () => {
        await loadLeads()
      }, 200)
    }
    const scheduleReloadTasks = () => {
      if (reloadTasksTimer) clearTimeout(reloadTasksTimer)
      reloadTasksTimer = setTimeout(async () => {
        await loadTasks()
      }, 200)
    }
    const fetchLeadById = async (id) => {
      try {
        const response = await api.get(`/leads/${id}`, { params: { _t: Date.now() } })
        return response.data
      } catch (e) {
        return null
      }
    }
    const fetchTaskById = async (id) => {
      try {
        const response = await api.get(`/tasks/${id}`, { params: { _t: Date.now() } })
        return response.data
      } catch (e) {
        return null
      }
    }

    const startHeartbeat = () => {
      stopHeartbeat()
      wsHeartbeatTimer = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send('ping')
            wsPongTimer = setTimeout(() => {
              console.log('[WS] Pong timeout - conexao perdida')
              if (ws) ws.close()
            }, WS_PONG_TIMEOUT)
          } catch (e) {
            console.log('[WS] Erro ao enviar ping:', e)
          }
        }
      }, WS_HEARTBEAT_INTERVAL)
    }

    const stopHeartbeat = () => {
      if (wsHeartbeatTimer) { clearInterval(wsHeartbeatTimer); wsHeartbeatTimer = null }
      if (wsPongTimer) { clearTimeout(wsPongTimer); wsPongTimer = null }
    }

    const scheduleReconnect = () => {
      if (wsReconnectTimer || wsIntentionalClose) return
      const delay = Math.min(
        WS_INITIAL_RECONNECT_DELAY * Math.pow(2, wsReconnectAttempts),
        WS_MAX_RECONNECT_DELAY
      )
      console.log(`[WS] Reconectando em ${delay}ms (tentativa ${wsReconnectAttempts + 1})`)
      wsReconnectTimer = setTimeout(() => {
        wsReconnectTimer = null
        wsReconnectAttempts++
        connectWebSocket()
      }, delay)
    }

    const reloadAllDataAfterReconnect = async () => {
      try {
        console.log('[WS] Recarregando dados apos reconexao...')
        await Promise.all([loadColumns(), loadTasks(), loadLeads()])
        console.log('[WS] Dados recarregados com sucesso')
      } catch (e) {
        console.error('[WS] Erro ao recarregar dados:', e)
      }
    }

    const disposeWebSocket = () => {
      if (!ws) return
      const old = ws
      ws = null
      old.onopen = null
      old.onclose = null
      old.onerror = null
      old.onmessage = null
      try {
        old.close(1000, 'replaced')
      } catch (_) {
        /* ignore */
      }
    }

    const connectWebSocket = () => {
      try {
        const token = localStorage.getItem('token')
        if (!token || !workspaceId.value) {
          console.log('[WS] Token ou workspace ausente')
          return
        }

        disposeWebSocket()
        if (wsReconnectTimer) {
          clearTimeout(wsReconnectTimer)
          wsReconnectTimer = null
        }

        const generation = ++wsConnectGeneration
        const wsUrl = buildKanbanWebSocketUrl(workspaceId.value, token)
        if (!wsUrl) {
          console.log('[WS] URL invalida')
          return
        }

        wsIntentionalClose = false
        console.log('[WS] Conectando:', wsUrl)
        const socket = new WebSocket(wsUrl)
        ws = socket

        socket.onopen = () => {
          if (generation !== wsConnectGeneration || ws !== socket) return
          console.log('[WS] Conectado com sucesso!')
          wsConnected.value = true
          wsReconnectAttempts = 0
          startHeartbeat()
          if (wsHasConnectedBefore) {
            reloadAllDataAfterReconnect()
          }
          wsHasConnectedBefore = true
        }
        socket.onclose = (event) => {
          if (generation !== wsConnectGeneration) return
          console.log('[WS] Desconectado. Code:', event.code, 'Reason:', event.reason)
          wsConnected.value = false
          stopHeartbeat()
          if (ws === socket) ws = null
          if (!wsIntentionalClose) {
            scheduleReconnect()
          }
        }
        socket.onerror = (e) => {
          if (generation !== wsConnectGeneration) return
          console.log('[WS] Erro na conexao:', e)
        }
        socket.onmessage = async (evt) => {
          if (generation !== wsConnectGeneration || ws !== socket) return
          try {
            if (evt.data === 'pong') {
              if (wsPongTimer) { clearTimeout(wsPongTimer); wsPongTimer = null }
              return
            }
            const data = JSON.parse(evt.data || '{}')
            const type = data.type
            const payload = data.payload
            console.log('[WS] Evento recebido:', type)

            // Repassar eventos do WhatsApp para listeners globais (LeadDetailModal)
            if (type && type.startsWith('whatsapp.')) {
              try {
                const event = new CustomEvent(type, { detail: payload })
                window.dispatchEvent(event)
                if (type === 'whatsapp.message.received' || type === 'whatsapp.message.sent') {
                  try {
                    const p = typeof payload === 'string' ? JSON.parse(payload) : (payload || {})
                    const leadId = p?.leadId
                    const direction = String(p?.direction || '').toUpperCase()
                    if (type === 'whatsapp.message.received' && leadId && direction === 'INCOMING') {
                      const idx = leads.value.findIndex(l => Number(l.id) === Number(leadId))
                      if (idx !== -1) {
                        const cur = Number(leads.value[idx].mensagensNaoLidas || 0)
                        leads.value[idx] = { ...leads.value[idx], mensagensNaoLidas: cur + 1 }
                      } else {
                        await loadLeads()
                      }
                      // Contador não lidas; som/desktop via listener global (CustomEvent acima)
                    } else if (type === 'whatsapp.message.sent' && leadId) {
                      const idx = leads.value.findIndex(l => Number(l.id) === Number(leadId))
                      if (idx === -1) {
                        await loadLeads()
                      }
                    } else {
                      await loadLeads()
                    }
                  } catch {
                    await loadLeads()
                  }
                }
              } catch (e) {
                console.error('[WS] Erro ao processar evento WhatsApp:', e)
              }
            }

            // Atualizacoes para TAREFAS
            if (workspace.value?.tipo === 'TAREFAS') {
              if (type === 'task.updated' && payload && payload.type) {
                const subTypes = ['anexo_added', 'anexo_deleted', 'atividade_iniciada', 'atividade_parada', 'agendamento_criado', 'agendamento_cancelado', 'agendamento_deletado']
                if (subTypes.includes(payload.type) && payload.taskId) {
                  const fresh = await fetchTaskById(payload.taskId)
                  if (fresh) {
                    const idx = tasks.value.findIndex(t => Number(t.id) === Number(fresh.id))
                    if (idx !== -1) {
                      Object.assign(tasks.value[idx], fresh)
                    }
                    if (selectedTask.value && selectedTask.value.id === fresh.id) {
                      selectedTask.value = { ...selectedTask.value, ...fresh }
                    }
                  }
                  return
                }
              }
              if (type === 'task.updated' && payload && payload.id) {
                const idx = tasks.value.findIndex(t => Number(t.id) === Number(payload.id))
                if (idx !== -1) {
                  Object.assign(tasks.value[idx], payload)
                  if (selectedTask.value && selectedTask.value.id === payload.id) {
                    selectedTask.value = { ...selectedTask.value, ...payload }
                  }
                  return
                }
                const fresh = await fetchTaskById(payload.id)
                if (fresh && fresh.id) {
                  const idx2 = tasks.value.findIndex(t => Number(t.id) === Number(fresh.id))
                  if (idx2 !== -1) {
                    Object.assign(tasks.value[idx2], fresh)
                  } else {
                    tasks.value.push(fresh)
                  }
                  if (selectedTask.value && selectedTask.value.id === fresh.id) {
                    selectedTask.value = { ...selectedTask.value, ...fresh }
                  }
                  return
                }
                return
              }
              if (type === 'task.moved' && payload && payload.taskId && payload.colunaId) {
                const response = await api.get(`/tasks/workspace/${workspaceId.value}`, { params: { _t: Date.now() } })
                const freshTasks = response.data
                freshTasks.forEach(freshTask => {
                  const idx = tasks.value.findIndex(t => t.id === freshTask.id)
                  if (idx !== -1 && (tasks.value[idx].colunaId !== freshTask.colunaId || tasks.value[idx].posicao !== freshTask.posicao)) {
                    Object.assign(tasks.value[idx], {
                      colunaId: freshTask.colunaId,
                      posicao: freshTask.posicao
                    })
                  }
                })
                return
              }
              if (type === 'task.archived' && payload && payload.taskId) {
                tasks.value = tasks.value.filter(t => Number(t.id) !== Number(payload.taskId))
                if (selectedTask.value && Number(selectedTask.value.id) === Number(payload.taskId)) {
                  showTaskDetailModal.value = false
                  selectedTask.value = null
                }
                return
              }
              if (type === 'task.chat.message' && payload && payload.taskId) {
                try {
                  const event = new CustomEvent('task.chat.message', { detail: payload })
                  window.dispatchEvent(event)
                } catch (e) {
                  console.error('[WS] Erro ao repassar evento de chat da task:', e)
                }
                return
              }
              if (type === 'task.created' || type === 'task.deleted' || type === 'task.reordered' || type === 'task.status') {
                scheduleReloadTasks()
                return
              }
            } else {
              // Atualizacoes para CRM (leads)
              if (type === 'lead.updated' && payload) {
                const leadId = payload.id || payload.leadId
                if (leadId) {
                  const fresh = await fetchLeadById(leadId)
                  if (fresh) {
                    const idx = leads.value.findIndex(l => Number(l.id) === Number(fresh.id))
                    if (idx !== -1) {
                      Object.assign(leads.value[idx], fresh)
                    } else {
                      leads.value.push(fresh)
                    }
                    if (selectedLead.value && Number(selectedLead.value.id) === Number(fresh.id)) {
                      selectedLead.value = { ...selectedLead.value, ...fresh }
                    }
                  }
                  return
                }
              }
              if (type === 'lead.moved' && payload && payload.leadId && payload.colunaId) {
                const response = await api.get(`/leads/workspace/${workspaceId.value}`, { params: { _t: Date.now() } })
                const freshLeads = response.data
                freshLeads.forEach(freshLead => {
                  const idx = leads.value.findIndex(l => l.id === freshLead.id)
                  if (idx !== -1 && (leads.value[idx].colunaId !== freshLead.colunaId || leads.value[idx].posicao !== freshLead.posicao)) {
                    Object.assign(leads.value[idx], {
                      colunaId: freshLead.colunaId,
                      posicao: freshLead.posicao
                    })
                  }
                })
                return
              }
              if (type === 'lead.archived' && payload && payload.leadId) {
                leads.value = leads.value.filter(l => Number(l.id) !== Number(payload.leadId))
                if (selectedLead.value && Number(selectedLead.value.id) === Number(payload.leadId)) {
                  showLeadDetailModal.value = false
                  selectedLead.value = null
                }
                return
              }
              if (type === 'lead.chat.message' && payload && payload.leadId) {
                try {
                  const event = new CustomEvent('lead.chat.message', { detail: payload })
                  window.dispatchEvent(event)
                } catch (e) {
                  console.error('[WS] Erro ao repassar evento de chat do lead:', e)
                }
                return
              }
              if (type === 'lead.created' || type === 'lead.deleted' || type === 'lead.reordered' || type === 'lead.status') {
                scheduleReloadLeads()
                return
              }
            }
            
            // Atualizacoes de COLUNAS (aplicavel a ambos TAREFAS e CRM)
            if (type === 'column.created' && payload) {
              await loadColumns()
              return
            }
            if (type === 'column.updated' && payload && payload.id) {
              const idx = columns.value.findIndex(c => Number(c.id) === Number(payload.id))
              if (idx !== -1) {
                columns.value[idx] = { ...columns.value[idx], ...payload }
                columns.value = [...columns.value]
              }
              await loadColumns()
              return
            }
            if (type === 'column.deleted' && payload && payload.id) {
              columns.value = columns.value.filter(c => Number(c.id) !== Number(payload.id))
              return
            }
            if (type === 'column.reordered' && payload) {
              await loadColumns()
              return
            }
          } catch (e) {
            console.error('[WS] Mensagem invalida:', evt.data)
          }
        }
      } catch (e) {
        console.error('[WS] Erro ao conectar:', e)
        wsConnected.value = false
        scheduleReconnect()
      }
    }
    const columns = ref([])
    const tasks = ref([])
    const leads = ref([])
    const orderMap = ref({}) // { [columnId]: number[] }
    const showCreateColumnModal = ref(false)
    const showEditColumnModal = ref(false)
    const showCreateCardModal = ref(false)
    const showArchivedLeadsModal = ref(false)
    const arquivadosLeads = ref([])
    const arquivadosTotal = ref(0)
    const arquivadosPage = ref(0)
    const arquivadosTotalPages = ref(0)
    const arquivadosSearch = ref('')
    const arquivadosBusyId = ref(null)
    const loadingArquivados = ref(false)
    const ARCHIVED_LEADS_PAGE_SIZE = 30
    const showTaskDetailModal = ref(false)
    const showLeadDetailModal = ref(false)
    const showSchedulerModal = ref(false)
    const showTogglesPopover = ref(false)
    const selectedTask = ref(null)
    const selectedLead = ref(null)
    const selectedColumn = ref(null)
    const selectedDate = ref(new Date())
    const workspaceTags = ref([])
    const workspaceUsers = ref([])
    const workspaceMotivos = ref([])

    // Filtro de texto para cards/leads
    const searchText = ref('')
    const searchTerm = computed(() => searchText.value.trim().toLowerCase())

    const textIncludes = (value, term) => {
      if (!term) return true
      if (!value) return false
      return String(value).toLowerCase().includes(term)
    }

    const matchesTaskSearch = (task) => {
      const term = searchTerm.value
      if (!term) return true

      const candidates = [
        task.titulo,
        task.descricao,
        task.status,
        task.clienteNome || task.cliente || task.nomeCliente,
        task.colunaNome
      ]

      return candidates.some(value => textIncludes(value, term))
    }

    const matchesLeadSearch = (lead) => {
      const term = searchTerm.value
      if (!term) return true

      const candidates = [
        lead.nome,
        lead.empresa,
        lead.cargo,
        lead.email,
        lead.telefone,
        lead.celular,
        lead.status,
        lead.origem,
        lead.cnpj,
        lead.descricao,
        (lead.usuarioAtribuido && (lead.usuarioAtribuido.nomeCompleto || lead.usuarioAtribuido.nome)) || null
      ]

      return candidates.some(value => textIncludes(value, term))
    }

    const filteredTasks = computed(() => {
      if (!searchTerm.value) return tasks.value
      return tasks.value.filter(matchesTaskSearch)
    })

    const filteredLeads = computed(() => {
      if (!searchTerm.value) return leads.value
      return leads.value.filter(matchesLeadSearch)
    })

    const getDefaultPreviewVisibility = () => ({
      showDescription: true,
      showTagsExpanded: true,
      showUsers: true,
      showActivityControls: true,
      showSchedule: true,
      showChecklist: true,
      showAttachments: true,
      showCreatedAt: true,
      showUpdatedAt: true,
      showFinanceiro: true,
      showTaskProgress: true
    })

    const previewOptionsVisibility = ref(getDefaultPreviewVisibility())
    const defaultKanbanPermissions = () => ({
      canCreateColumns: true,
      canMoveColumns: true,
      canDeleteColumns: true,
      canMoveCards: true,
      canArchiveCards: true,
      showDescription: true,
      showTagsExpanded: true,
      showUsers: true,
      showActivityControls: true,
      showSchedule: true,
      showChecklist: true,
      showAttachments: true,
      showCreatedAt: true,
      showUpdatedAt: true,
      showFinanceiro: true,
      showTaskProgress: true,
      showCustomForms: true,
      lockedColumnIds: []
    })
    const effectivePermissions = ref(defaultKanbanPermissions())

    const isPreviewOptionVisible = (key) => {
      return previewOptionsVisibility.value?.[key] !== false
    }

    const planAllowsPreviewKey = (key) => {
      const featureKey = KANBAN_PREVIEW_PLAN_KEYS[key]
      return !featureKey || planoFeatures.can(featureKey)
    }

    const canUseKanbanPreviewMenu = () => planoFeatures.can('kanbanPreviewMenu')

    const canShowPreviewToggle = (key) => {
      if (!canUseKanbanPreviewMenu()) return false
      if (!isPreviewOptionVisible(key)) return false
      if (!planAllowsPreviewKey(key)) return false
      const permKey = key
      if (effectivePermissions.value && permKey in effectivePermissions.value) {
        return effectivePermissions.value[permKey] !== false
      }
      return true
    }

    const isColumnLocked = (columnId) => {
      const ids = effectivePermissions.value?.lockedColumnIds || []
      return Array.isArray(ids) && ids.includes(columnId)
    }

    const loadKanbanPermissions = async () => {
      effectivePermissions.value = defaultKanbanPermissions()
      try {
        const response = await api.get(`/workspace-acesso/workspace/${workspaceId.value}/kanban-permissions/me`)
        const backendPermissions = response?.data?.permissions
        const isFullAccess = response?.data?.isFullAccess === true
        if (isFullAccess) {
          effectivePermissions.value = defaultKanbanPermissions()
          return
        }
        if (backendPermissions && typeof backendPermissions === 'object') {
          effectivePermissions.value = {
            ...defaultKanbanPermissions(),
            ...backendPermissions,
            lockedColumnIds: Array.isArray(backendPermissions.lockedColumnIds)
              ? backendPermissions.lockedColumnIds
              : []
          }
        }
      } catch (error) {
        console.warn('[KANBAN] Não foi possível carregar permissões de grupo. Usando padrão.', error)
      }
    }

    const loadPreviewOptionsVisibility = async () => {
      previewOptionsVisibility.value = getDefaultPreviewVisibility()
      try {
        const response = await api.get(`/workspace-config/${workspaceId.value}/ui-config`)
        const taskPreviewJson = response?.data?.taskPreviewJson
        if (!taskPreviewJson || typeof taskPreviewJson !== 'string') return

        const parsed = JSON.parse(taskPreviewJson)
        const visibility = parsed?.visibility
        if (!visibility || typeof visibility !== 'object') return

        previewOptionsVisibility.value = {
          ...getDefaultPreviewVisibility(),
          ...Object.keys(getDefaultPreviewVisibility()).reduce((acc, key) => {
            acc[key] = visibility[key] !== false
            return acc
          }, {})
        }
      } catch (error) {
        console.warn('[KANBAN] Não foi possível carregar visibilidade do preview. Usando padrão.', error)
      }
    }

    // Toggle para datas de criação e atualização
    const getCreatedAtStorageKey = () => `kanban-show-created-at:${workspaceId.value}`
    const getUpdatedAtStorageKey = () => `kanban-show-updated-at:${workspaceId.value}`

    const loadShowCreatedAt = () => {
      try {
        const saved = localStorage.getItem(getCreatedAtStorageKey())
        return saved === 'true'
      } catch (e) {
        return false
      }
    }

    const loadShowUpdatedAt = () => {
      try {
        const saved = localStorage.getItem(getUpdatedAtStorageKey())
        return saved === 'true'
      } catch (e) {
        return false
      }
    }

    const showCreatedAt = ref(loadShowCreatedAt())
    const showUpdatedAt = ref(loadShowUpdatedAt())

    const toggleShowCreatedAt = () => {
      showCreatedAt.value = !showCreatedAt.value
      try {
        localStorage.setItem(getCreatedAtStorageKey(), showCreatedAt.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de data de criação:', e)
      }
    }

    const toggleShowUpdatedAt = () => {
      showUpdatedAt.value = !showUpdatedAt.value
      try {
        localStorage.setItem(getUpdatedAtStorageKey(), showUpdatedAt.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de última alteração:', e)
      }
    }
    
    // Toggle para mostrar descrição - carregar do localStorage
    const getDescriptionStorageKey = () => `kanban-show-description:${workspaceId.value}`
    const loadShowDescription = () => {
      try {
        const saved = localStorage.getItem(getDescriptionStorageKey())
        return saved === 'true' // converter string para boolean
      } catch (e) {
        return false
      }
    }
    const showDescription = ref(loadShowDescription())
    
    // Salvar no localStorage quando mudar
    const toggleDescription = () => {
      showDescription.value = !showDescription.value
      try {
        localStorage.setItem(getDescriptionStorageKey(), showDescription.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de descrição:', e)
      }
    }
    
    // Toggle para expandir tags - carregar do localStorage
    const getTagsStorageKey = () => `kanban-show-tags-expanded:${workspaceId.value}`
    const loadShowTagsExpanded = () => {
      try {
        const saved = localStorage.getItem(getTagsStorageKey())
        return saved === 'true' // converter string para boolean
      } catch (e) {
        return false
      }
    }
    const showTagsExpanded = ref(loadShowTagsExpanded())
    
    // Salvar no localStorage quando mudar
    const toggleTagsExpanded = () => {
      showTagsExpanded.value = !showTagsExpanded.value
      try {
        localStorage.setItem(getTagsStorageKey(), showTagsExpanded.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de tags:', e)
      }
    }
    
    // Toggle para mostrar usuários atribuídos - carregar do localStorage
    const getUsersStorageKey = () => `kanban-show-users:${workspaceId.value}`
    const loadShowUsers = () => {
      try {
        const saved = localStorage.getItem(getUsersStorageKey())
        return saved === 'true' // converter string para boolean
      } catch (e) {
        return false
      }
    }
    const showUsers = ref(loadShowUsers())
    
    // Salvar no localStorage quando mudar
    const toggleUsers = () => {
      showUsers.value = !showUsers.value
      try {
        localStorage.setItem(getUsersStorageKey(), showUsers.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de usuários:', e)
      }
    }
    
    // Toggle para controles de atividade - carregar do localStorage
    const getActivityControlsStorageKey = () => `kanban-show-activity-controls:${workspaceId.value}`
    const loadShowActivityControls = () => {
      try {
        const saved = localStorage.getItem(getActivityControlsStorageKey())
        return saved === 'true' // converter string para boolean
      } catch (e) {
        return false
      }
    }
    const showActivityControls = ref(loadShowActivityControls())
    
    // Salvar no localStorage quando mudar
    const toggleActivityControls = () => {
      showActivityControls.value = !showActivityControls.value
      try {
        localStorage.setItem(getActivityControlsStorageKey(), showActivityControls.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de controles de atividade:', e)
      }
    }
    
    // Toggle para agendamento - carregar do localStorage
    const getScheduleStorageKey = () => `kanban-show-schedule:${workspaceId.value}`
    const loadShowSchedule = () => {
      try {
        const saved = localStorage.getItem(getScheduleStorageKey())
        return saved === 'true' // converter string para boolean
      } catch (e) {
        return false
      }
    }
    const showSchedule = ref(loadShowSchedule())
    
    // Salvar no localStorage quando mudar
    const toggleSchedule = () => {
      showSchedule.value = !showSchedule.value
      try {
        localStorage.setItem(getScheduleStorageKey(), showSchedule.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de agendamento:', e)
      }
    }
    
    // Toggle para checklist - carregar do localStorage
    const getChecklistStorageKey = () => `kanban-show-checklist:${workspaceId.value}`
    const loadShowChecklist = () => {
      try {
        const saved = localStorage.getItem(getChecklistStorageKey())
        return saved === 'true' // converter string para boolean
      } catch (e) {
        return false
      }
    }
    const showChecklist = ref(loadShowChecklist())
    
    // Salvar no localStorage quando mudar
    const toggleChecklist = () => {
      showChecklist.value = !showChecklist.value
      try {
        localStorage.setItem(getChecklistStorageKey(), showChecklist.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de checklist:', e)
      }
    }
    
    // Toggle para anexos - carregar do localStorage
    const getAttachmentsStorageKey = () => `kanban-show-attachments:${workspaceId.value}`
    const loadShowAttachments = () => {
      try {
        const saved = localStorage.getItem(getAttachmentsStorageKey())
        return saved === 'true' // converter string para boolean
      } catch (e) {
        return false
      }
    }
    const showAttachments = ref(loadShowAttachments())
    
    // Salvar no localStorage quando mudar
    const toggleAttachments = () => {
      showAttachments.value = !showAttachments.value
      try {
        localStorage.setItem(getAttachmentsStorageKey(), showAttachments.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de anexos:', e)
      }
    }
    
    // Toggle para detalhes do contato (CRM) - carregar do localStorage
    const getContactDetailsStorageKey = () => `kanban-show-contact-details:${workspaceId.value}`
    const loadShowContactDetails = () => {
      try {
        const saved = localStorage.getItem(getContactDetailsStorageKey())
        return saved === 'true'
      } catch (e) {
        return false
      }
    }
    const showContactDetails = ref(loadShowContactDetails())
    
    const toggleContactDetails = () => {
      showContactDetails.value = !showContactDetails.value
      try {
        localStorage.setItem(getContactDetailsStorageKey(), showContactDetails.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de detalhes do contato:', e)
      }
    }
    
    // Toggle para informações do negócio (CRM) - carregar do localStorage
    const getBusinessInfoStorageKey = () => `kanban-show-business-info:${workspaceId.value}`
    const loadShowBusinessInfo = () => {
      try {
        const saved = localStorage.getItem(getBusinessInfoStorageKey())
        return saved === 'true'
      } catch (e) {
        return false
      }
    }
    const showBusinessInfo = ref(loadShowBusinessInfo())
    
    const toggleBusinessInfo = () => {
      showBusinessInfo.value = !showBusinessInfo.value
      try {
        localStorage.setItem(getBusinessInfoStorageKey(), showBusinessInfo.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de informações do negócio:', e)
      }
    }

    const whatsappNotifySound = ref(false)
    const whatsappNotifyDesktop = ref(false)
    const notificationPermission = ref(getNotificationPermission())
    const notificationPermissionHint = computed(() =>
      formatNotificationPermissionHint(t)
    )

    const refreshNotificationPermission = () => {
      notificationPermission.value = getNotificationPermission()
    }

    const enableDesktopNotifyPref = () => {
      whatsappNotifyDesktop.value = true
      saveWhatsAppNotifyPref(authStore.user?.id, 'desktop', true)
    }

    const disableDesktopNotifyPref = () => {
      whatsappNotifyDesktop.value = false
      saveWhatsAppNotifyPref(authStore.user?.id, 'desktop', false)
    }

    const showDesktopTestOrWarn = () => {
      const shown = showTestDesktopNotification(t)
      if (!shown) {
        const origin = typeof window !== 'undefined' ? window.location.origin : ''
        Swal.fire({
          title: t('kanban.page.toggles.whatsappNotify.testFailedTitle'),
          text: t('kanban.page.toggles.whatsappNotify.testFailedText', { origin }),
          icon: 'warning',
          confirmButtonText: 'OK'
        })
      }
    }

    const reloadWhatsappNotifyPrefs = () => {
      refreshNotificationPermission()
      const prefs = loadWhatsAppNotifyPrefs(authStore.user?.id)
      whatsappNotifySound.value = prefs.sound
      let desktop = prefs.desktop
      if (desktop && notificationPermission.value !== 'granted') {
        desktop = false
        saveWhatsAppNotifyPref(authStore.user?.id, 'desktop', false)
      }
      whatsappNotifyDesktop.value = desktop
    }

    const toggleWhatsappNotifySound = () => {
      whatsappNotifySound.value = !whatsappNotifySound.value
      saveWhatsAppNotifyPref(authStore.user?.id, 'sound', whatsappNotifySound.value)
      if (whatsappNotifySound.value) {
        unlockAudioOnUserGesture()
      }
    }

    const toggleWhatsappNotifyDesktop = () => {
      const next = !whatsappNotifyDesktop.value
      if (!next) {
        disableDesktopNotifyPref()
        return
      }

      // requestPermission() deve iniciar no mesmo turno do clique (sem async/await antes)
      requestDesktopNotificationPermissionFromGesture().then((perm) => {
        refreshNotificationPermission()
        if (perm !== 'granted') {
          disableDesktopNotifyPref()
          const origin = typeof window !== 'undefined' ? window.location.origin : ''
          if (perm === 'unsupported') {
            Swal.fire({
              title: t('kanban.page.toggles.whatsappNotify.permissionUnsupportedTitle'),
              text: t('kanban.page.toggles.whatsappNotify.permissionUnsupportedText'),
              icon: 'info',
              confirmButtonText: 'OK'
            })
          } else {
            Swal.fire({
              title: t('kanban.page.toggles.whatsappNotify.permissionDeniedTitle'),
              text: t('kanban.page.toggles.whatsappNotify.permissionDeniedText', { origin }),
              icon: 'info',
              confirmButtonText: 'OK'
            })
          }
          return
        }
        enableDesktopNotifyPref()
        showDesktopTestOrWarn()
      })
    }

    const handleKanbanUserGesture = () => {
      unlockAudioOnUserGesture()
    }
    
    // Toggle "Is Financeiro" (apenas TAREFAS) - carregar do localStorage
    const getFinanceiroStorageKey = () => `kanban-show-financeiro:${workspaceId.value}`
    const loadShowFinanceiro = () => {
      try {
        const saved = localStorage.getItem(getFinanceiroStorageKey())
        return saved === 'true'
      } catch (e) {
        return false
      }
    }
    const showFinanceiro = ref(loadShowFinanceiro())
    
    const toggleFinanceiro = () => {
      showFinanceiro.value = !showFinanceiro.value
      try {
        localStorage.setItem(getFinanceiroStorageKey(), showFinanceiro.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de modo financeiro:', e)
      }
    }

    const getTaskProgressStorageKey = () => `kanban-show-task-progress:${workspaceId.value}`
    const loadShowTaskProgress = () => {
      try {
        const saved = localStorage.getItem(getTaskProgressStorageKey())
        return saved === 'true'
      } catch (e) {
        return false
      }
    }
    const showTaskProgress = ref(loadShowTaskProgress())

    const toggleTaskProgress = () => {
      showTaskProgress.value = !showTaskProgress.value
      try {
        localStorage.setItem(getTaskProgressStorageKey(), showTaskProgress.value.toString())
      } catch (e) {
        console.error('Erro ao salvar preferência de progresso da task:', e)
      }
    }
    
    // Drag entre colunas gerenciado pelo SortableJS
    
    // Computed
    const workspace = computed(() => workspaceStore.currentWorkspace)

    const canEditCrmLead = computed(() => {
      if (workspace.value?.tipo !== 'CRM') return true
      return planoFeatures.can('crmInformacoesContato')
    })

    const promptCrmLeadUpgrade = async () => {
      const result = await Swal.fire({
        title: t('kanban.page.swal.planBlocked.title'),
        html: t('kanban.page.swal.planBlocked.html'),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: t('kanban.page.swal.planBlocked.confirmUpgrade'),
        cancelButtonText: t('kanban.page.swal.planBlocked.close'),
        confirmButtonColor: '#2563eb',
        cancelButtonColor: '#6b7280',
        reverseButtons: true
      })
      if (result.isConfirmed) {
        await router.push('/upgrade')
      }
    }
    
    // Métodos
    const loadColumns = async () => {
      try {
        const response = await api.get(`/colunas-kanban/workspace/${workspaceId.value}`)
        columns.value = response.data
        console.log('Colunas carregadas:', columns.value)
        
        // Verificar se existe coluna padrão (BackLog)
        const hasDefaultColumn = columns.value.some(col => col.isPadrao)
        if (!hasDefaultColumn) {
          console.log('Coluna padrão BackLog não encontrada, criando...')
          await createDefaultColumn()
        }
      } catch (error) {
        console.error('Erro ao carregar colunas:', error)
        // Se der erro, tentar criar coluna padrão
        await createDefaultColumn()
      }
    }
    
    const createDefaultColumn = async () => {
      try {
        console.log('Criando coluna padrão BackLog para workspace:', workspaceId.value)
        const response = await api.post(`/colunas-kanban/workspace/${workspaceId.value}/padrao`)
        console.log('Coluna padrão criada:', response.data)
        await loadColumns() // Recarregar colunas
      } catch (error) {
        console.error('Erro ao criar coluna padrão:', error)
      }
    }
    
    const loadTasks = async (skipExtras = false) => {
      if (workspace.value?.tipo === 'TAREFAS') {
        try {
          const response = await api.get(`/tasks/workspace/${workspaceId.value}`, { params: { _t: Date.now() } })
          tasks.value = response.data
          console.log('📋 Tasks carregadas:', tasks.value)
          console.log('📅 [KanbanPage.loadTasks] Datas das tasks:', tasks.value.map(t => ({
            id: t.id,
            dataCriacao: t.dataCriacao,
            dataUpdate: t.dataUpdate
          })))
          
          // Debug: verificar agendamentos nas tasks
          tasks.value.forEach(task => {
            if (task.dataAgendamento || task.horario) {
              console.log('📅 Task com agendamento encontrada:', {
                id: task.id,
                titulo: task.titulo,
                dataAgendamento: task.dataAgendamento,
                horario: task.horario,
                motivo: task.motivo
              })
            }
          })
          
          // ✅ Carregar agendamentos e anexos apenas se não for movimentação
          if (!skipExtras) {
            await loadTasksSchedules()
            await loadTasksAnexos()
          }
        } catch (error) {
          console.error('Erro ao carregar tasks:', error)
        }
      }
    }
    
    // Função para carregar agendamentos de todas as tasks
    const loadTasksSchedules = async () => {
      console.log('📅 Carregando agendamentos para todas as tasks...')
      
      const token = localStorage.getItem('token')
      if (!token) return
      
      try {
        // Carregar agendamentos para cada task
        const schedulePromises = tasks.value.map(async (task) => {
          try {
            const response = await api.get(`/agendamentos/task/${task.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
            
            if (response.data && response.data.length > 0) {
              // Pegar o agendamento mais recente
              const ultimoAgendamento = response.data[0]
              
              // ✅ Usar Object.assign para atualização suave (sem flickering)
              Object.assign(task, {
                dataAgendamento: ultimoAgendamento.dataAgendamento,
                horario: ultimoAgendamento.horario,
                motivo: ultimoAgendamento.motivo
              })
              
              console.log('📅 Agendamento carregado para task', task.id, ':', {
                dataAgendamento: task.dataAgendamento,
                horario: task.horario,
                motivo: task.motivo
              })
            }
          } catch (error) {
            // Silenciar erro para tasks sem agendamento
            if (error.response?.status !== 404) {
              console.error(`Erro ao carregar agendamento da task ${task.id}:`, error)
            }
          }
        })
        
        await Promise.all(schedulePromises)
        console.log('✅ Agendamentos carregados para todas as tasks')
        
        // ✅ REMOVIDO: Não forçar recriação do array (causa flickering)
        // tasks.value = [...tasks.value]
      } catch (error) {
        console.error('Erro ao carregar agendamentos:', error)
      }
    }
    
    // 📎 Função para carregar anexos de todas as tasks
    const loadTasksAnexos = async () => {
      console.log('📎 Carregando anexos para todas as tasks...')
      
      const token = localStorage.getItem('token')
      if (!token) return
      
      try {
        // Carregar anexos para cada task
        const anexosPromises = tasks.value.map(async (task) => {
          try {
            const response = await api.get(`/anexos/task/${task.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
            
            if (response.data && response.data.length > 0) {
              // ✅ Usar Object.assign para atualização suave (sem flickering)
              Object.assign(task, { anexos: response.data })
              console.log('📎 Anexos carregados para task', task.id, ':', response.data.length, 'arquivo(s)')
            } else {
              Object.assign(task, { anexos: [] })
            }
          } catch (error) {
            // Silenciar erro para tasks sem anexos
            Object.assign(task, { anexos: [] })
          }
        })
        
        await Promise.all(anexosPromises)
        console.log('✅ Anexos carregados para todas as tasks')
        
        // ✅ REMOVIDO: Não forçar recriação do array (causa flickering)
        // tasks.value = [...tasks.value]
      } catch (error) {
        console.error('Erro ao carregar anexos:', error)
      }
    }
    
    const loadLeads = async (skipExtras = false) => {
      if (workspace.value?.tipo === 'CRM') {
        try {
          const response = await api.get(`/leads/workspace/${workspaceId.value}`, { 
            params: { _t: Date.now() }
          })
          leads.value = response.data
          console.log('📊 [KanbanPage.loadLeads] Leads carregados:', leads.value.length, 'leads')
          console.log('📊 [KanbanPage.loadLeads] TODOS OS LEADS:', leads.value.map(l => ({ 
            id: l.id, 
            nome: l.nome, 
            mensagensNaoLidas: l.mensagensNaoLidas,
            dataCriacao: l.dataCriacao,
            dataUpdate: l.dataUpdate
          })))
          
          // Log de leads com mensagens não lidas
          const leadsComMsgNaoLidas = leads.value.filter(l => l.mensagensNaoLidas > 0)
          if (leadsComMsgNaoLidas.length > 0) {
            console.log('🔔 [KanbanPage.loadLeads] Leads COM mensagens não lidas:', leadsComMsgNaoLidas.map(l => ({
              id: l.id,
              nome: l.nome,
              mensagensNaoLidas: l.mensagensNaoLidas
            })))
          } else {
            console.log('✅ [KanbanPage.loadLeads] NENHUM lead com mensagens não lidas')
          }
          
          // ✅ Carregar agendamentos e anexos apenas se não for movimentação
          if (!skipExtras) {
            await loadLeadsSchedules()
            await loadLeadsAnexos()
          }
        } catch (error) {
          console.error('Erro ao carregar leads:', error)
        }
      }
    }
    
    // 📅 Função para carregar agendamentos de todos os leads
    const loadLeadsSchedules = async () => {
      console.log('📅 Carregando agendamentos para todos os leads...')
      
      const token = localStorage.getItem('token')
      if (!token) return
      
      try {
        // Carregar agendamentos para cada lead
        const schedulePromises = leads.value.map(async (lead) => {
          try {
            const response = await api.get(`/agendamentos/lead/${lead.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
            
            if (response.data && response.data.length > 0) {
              // Pegar o agendamento mais recente
              const ultimoAgendamento = response.data[0]
              
              // ✅ Usar Object.assign para atualização suave (sem flickering)
              Object.assign(lead, {
                dataAgendamento: ultimoAgendamento.dataAgendamento,
                horario: ultimoAgendamento.horario,
                motivo: ultimoAgendamento.motivo
              })
              
              console.log('📅 Agendamento carregado para lead', lead.id, ':', {
                dataAgendamento: lead.dataAgendamento,
                horario: lead.horario,
                motivo: lead.motivo
              })
            }
          } catch (error) {
            // Silenciar erro para leads sem agendamento
            if (error.response?.status !== 404) {
              console.error(`Erro ao carregar agendamento do lead ${lead.id}:`, error)
            }
          }
        })
        
        await Promise.all(schedulePromises)
        console.log('✅ Agendamentos carregados para todos os leads')
        
        // ✅ REMOVIDO: Não forçar recriação do array (causa flickering)
        // leads.value = [...leads.value]
      } catch (error) {
        console.error('Erro ao carregar agendamentos dos leads:', error)
      }
    }
    
    // 📎 Função para carregar anexos de todos os leads
    const loadLeadsAnexos = async () => {
      console.log('📎 [ANEXOS] Iniciando carregamento de anexos para leads...')
      const token = localStorage.getItem('token')
      if (!token) {
        console.log('❌ [ANEXOS] Token não encontrado')
        return
      }
      
      try {
        // Carregar anexos para cada lead (usando endpoint correto /anexos-lead)
        const anexosPromises = leads.value.map(async (lead) => {
          try {
            console.log(`📎 [ANEXOS] Buscando anexos para lead ${lead.id} (${lead.nome})`)
            const response = await api.get(`/anexos-lead/lead/${lead.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
            
            console.log(`📎 [ANEXOS] Resposta para lead ${lead.id}:`, response.data)
            
            if (response.data && response.data.length > 0) {
              // ✅ Usar Object.assign para atualização suave (sem flickering)
              Object.assign(lead, { anexos: response.data })
              console.log(`✅ [ANEXOS] Lead ${lead.id} (${lead.nome}) tem ${response.data.length} anexo(s):`, response.data)
            } else {
              Object.assign(lead, { anexos: [] })
              console.log(`⚠️ [ANEXOS] Lead ${lead.id} (${lead.nome}) não tem anexos`)
            }
          } catch (error) {
            Object.assign(lead, { anexos: [] })
            if (error.response?.status === 404) {
              console.log(`⚠️ [ANEXOS] Lead ${lead.id} (${lead.nome}) não tem anexos (404)`)
            } else {
              console.error(`❌ [ANEXOS] Erro ao buscar anexos do lead ${lead.id}:`, error)
            }
          }
        })
        
        await Promise.all(anexosPromises)
        
        // Log resumido
        const leadsComAnexos = leads.value.filter(lead => lead.anexos && lead.anexos.length > 0)
        console.log(`📊 [ANEXOS] Resumo: ${leadsComAnexos.length} de ${leads.value.length} lead(s) com anexos`)
        
        if (leadsComAnexos.length > 0) {
          console.log('📋 [ANEXOS] Leads com anexos:')
          leadsComAnexos.forEach(lead => {
            console.log(`  - Lead ${lead.id} (${lead.nome}): ${lead.anexos.length} anexo(s)`)
          })
        }
        
        // ✅ REMOVIDO: Não forçar recriação do array (causa flickering)
        // leads.value = [...leads.value]
        console.log('✅ [ANEXOS] Anexos carregados para todos os leads')
      } catch (error) {
        console.error('❌ [ANEXOS] Erro geral ao carregar anexos dos leads:', error)
      }
    }
    
    const loadWorkspaceTags = async () => {
      // Carregar configurações tanto para TAREFAS quanto para CRM
      try {
        const response = await api.get(`/workspace-config/${workspaceId.value}/debug`)
        const config = response.data
        workspaceTags.value = config.categorias || []
        workspaceMotivos.value = config.motivosAgendamento || []
        console.log('✅ Tags do workspace carregadas:', workspaceTags.value.length)
        console.log('✅ Motivos de agendamento carregados:', workspaceMotivos.value.length)
        
        // Log detalhado dos motivos
        if (workspaceMotivos.value.length > 0) {
          console.log('📋 Motivos de agendamento:')
          workspaceMotivos.value.forEach((m, idx) => {
            console.log(`  ${idx + 1}. ID: ${m.id}, Nome: ${m.motivo || m.nome}, Icone: ${m.icone}, Cor: ${m.cor}`)
          })
        }
      } catch (error) {
        console.error('❌ Erro ao carregar configurações do workspace:', error)
        workspaceTags.value = []
        workspaceMotivos.value = []
      }
    }
    
    const loadWorkspaceUsers = async () => {
      // Carregar usuários tanto para TAREFAS quanto para CRM
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          return
        }

        const response = await api.get(`/workspace-acesso/workspace/${workspaceId.value}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        // Mapear dados da API para o formato esperado pelo frontend
        const usuariosApi = response.data || []
        workspaceUsers.value = usuariosApi.map(usuario => ({
          id: usuario.usuarioId,
          nomeCompleto: usuario.usuarioNome,
          email: usuario.usuarioEmail,
          fotoPerfil: resolveMediaUrl(usuario.usuarioFotoPerfil) || null,
          roleWorkspace: usuario.roleWorkspace,
          ativo: usuario.ativo
        }))
        
        console.log('✅ Usuários do workspace carregados:', workspaceUsers.value.length)
        console.log('📸 Usuários com foto:', workspaceUsers.value.filter(u => u.fotoPerfil).length)
        
      } catch (error) {
        console.error('❌ Erro ao carregar usuários do workspace:', error)
        workspaceUsers.value = []
      }
    }
    
    const getOrderKey = () => `kanban-order:${workspaceId.value}:${workspace.value?.tipo || ''}`

    const loadOrderMap = () => {
      try {
        const raw = localStorage.getItem(getOrderKey())
        return raw ? JSON.parse(raw) : {}
      } catch (e) {
        return {}
      }
    }

    const saveOrderMap = () => {
      try {
        localStorage.setItem(getOrderKey(), JSON.stringify(orderMap.value || {}))
      } catch (e) {}
    }

    const updateOrderForColumn = (columnId) => {
      if (!columnId) return
      if (workspace.value?.tipo === 'TAREFAS') {
        const items = tasks.value.filter(t => t.colunaId === columnId)
        orderMap.value = { ...orderMap.value, [columnId]: items.map(t => t.id) }
      } else {
        const items = leads.value.filter(l => l.colunaId === columnId)
        orderMap.value = { ...orderMap.value, [columnId]: items.map(l => l.id) }
      }
      saveOrderMap()
    }

    const recomputePositionsForColumn = (columnId) => {
      if (!columnId) return
      if (workspace.value?.tipo === 'TAREFAS') {
        const colItems = tasks.value.filter(t => t.colunaId === columnId)
        // ordena por ordem atual visual e reatribui posicao sequencial
        colItems.forEach((item, idx) => { item.posicao = idx + 1 })
      } else {
        const colItems = leads.value.filter(l => l.colunaId === columnId)
        colItems.forEach((item, idx) => { item.posicao = idx + 1 })
      }
    }

    const getCardsByColumn = (columnId) => {
      const sortFn = (a, b) => {
        const ap = a.posicao ?? Number.MAX_SAFE_INTEGER
        const bp = b.posicao ?? Number.MAX_SAFE_INTEGER
        if (ap !== bp) return ap - bp
        // fallback por dataCriacao se existir
        const ad = a.dataCriacao ? new Date(a.dataCriacao).getTime() : 0
        const bd = b.dataCriacao ? new Date(b.dataCriacao).getTime() : 0
        return ad - bd
      }
      if (workspace.value?.tipo === 'TAREFAS') {
        return filteredTasks.value
          .filter(task => task.colunaId === columnId)
          .slice()
          .sort(sortFn)
      } else {
        return filteredLeads.value
          .filter(lead => lead.colunaId === columnId)
          .slice()
          .sort(sortFn)
      }
    }
    
    // Controle para evitar chamadas duplicadas
    const movingCards = new Set()
    
    // Handler ÚNICO e SIMPLES para movimentação
    const handleMoveCard = async (cardId, newColumnId, toIndex = 0) => {
      if (!effectivePermissions.value.canMoveCards) {
        console.warn('Movimento bloqueado por permissão')
        return
      }
      if (isColumnLocked(newColumnId)) {
        console.warn('Coluna bloqueada para este grupo')
        return
      }
      const moveKey = `${cardId}`
      
      // Bloquear movimentos duplicados
      if (movingCards.has(moveKey)) {
        console.log('⏸️ Aguarde...card já está sendo movido')
        return
      }
      
      movingCards.add(moveKey)
      
      try {
        console.log('🚀 Movendo:', { cardId, newColumnId, toIndex })
        
        // Calcular posição final (1-based)
        const posicaoFinal = Math.max(1, toIndex + 1)
        
        if (workspace.value?.tipo === 'TAREFAS') {
          // Chamar API e aguardar resposta
          await api.put(`/tasks/${cardId}/mover/${newColumnId}`, null, { 
            params: { posicao: posicaoFinal } 
          })
          
          // ✅ Sincronizar posições de TODOS os cards afetados (sem perder agendamentos)
          const response = await api.get(`/tasks/workspace/${workspaceId.value}`, { params: { _t: Date.now() } })
          const freshTasks = response.data
          
          // Atualizar apenas posições e colunas (mantém agendamentos/anexos)
          freshTasks.forEach(freshTask => {
            const idx = tasks.value.findIndex(t => t.id === freshTask.id)
            if (idx !== -1 && (tasks.value[idx].colunaId !== freshTask.colunaId || tasks.value[idx].posicao !== freshTask.posicao)) {
              Object.assign(tasks.value[idx], {
                colunaId: freshTask.colunaId,
                posicao: freshTask.posicao
              })
            }
          })
          
          console.log('✅ Task movida e posições sincronizadas')
          
        } else {
          // Chamar API e aguardar resposta
          await api.put(`/leads/${cardId}/mover/${newColumnId}`, null, { 
            params: { posicao: posicaoFinal } 
          })
          
          // ✅ Sincronizar posições de TODOS os cards afetados (sem perder agendamentos)
          const response = await api.get(`/leads/workspace/${workspaceId.value}`, { params: { _t: Date.now() } })
          const freshLeads = response.data
          
          // Atualizar apenas posições e colunas (mantém agendamentos/anexos)
          freshLeads.forEach(freshLead => {
            const idx = leads.value.findIndex(l => l.id === freshLead.id)
            if (idx !== -1 && (leads.value[idx].colunaId !== freshLead.colunaId || leads.value[idx].posicao !== freshLead.posicao)) {
              Object.assign(leads.value[idx], {
                colunaId: freshLead.colunaId,
                posicao: freshLead.posicao
              })
            }
          })
          
          console.log('✅ Lead movido e posições sincronizadas')
        }
        
      } catch (error) {
        console.error('❌ Erro:', error)
        // Em caso de erro, recarregar do servidor
        if (workspace.value?.tipo === 'TAREFAS') {
          await loadTasks(true)
        } else {
          await loadLeads(true)
        }
      } finally {
        // Liberar após 200ms
        setTimeout(() => movingCards.delete(moveKey), 200)
      }
    }
    
    // Reordenação local usa o mesmo handler
    const handleReorderLocal = (cardId, columnId, newIndex) => {
      handleMoveCard(cardId, columnId, newIndex)
    }
    
    const handleUpdateCard = async (cardId, updates) => {
      try {
        if (workspace.value?.tipo === 'TAREFAS') {
          await api.put(`/tasks/${cardId}`, updates)
          await loadTasks()
        } else {
          await api.put(`/leads/${cardId}`, updates)
          await loadLeads()
        }
        console.log('Card atualizado com sucesso')
      } catch (error) {
        console.error('Erro ao atualizar card:', error)
      }
    }
    
    const handleDeleteCard = async (cardId) => {
      try {
        if (workspace.value?.tipo === 'TAREFAS') {
          await api.delete(`/tasks/${cardId}`)
          await loadTasks()
        } else {
          await api.delete(`/leads/${cardId}`)
          await loadLeads()
        }
        console.log('Card deletado com sucesso')
      } catch (error) {
        console.error('Erro ao deletar card:', error)
      }
    }
    
    const handleEditColumn = async (columnId) => {
      if (!effectivePermissions.value.canCreateColumns) {
        return
      }
      console.log('📝 handleEditColumn chamado para coluna:', columnId)
      // Encontrar a coluna para editar
      const column = columns.value.find(col => col.id === columnId)
      if (column) {
        console.log('📝 Coluna encontrada para edição:', column)
        selectedColumn.value = column
        showEditColumnModal.value = true
      } else {
        console.error('❌ Coluna não encontrada:', columnId)
      }
    }
    
    const handleDeleteColumn = async (columnId) => {
      if (!effectivePermissions.value.canDeleteColumns) {
        return
      }
      console.log('🗑️ handleDeleteColumn chamado para coluna:', columnId)
      
      const column = columns.value.find(col => col.id === columnId)
      if (!column) {
        console.error('❌ Coluna não encontrada para deletar:', columnId)
        return
      }
      
      console.log('🗑️ Coluna encontrada para deletar:', column)
      
      const result = await Swal.fire({
        title: t('kanban.page.swal.deleteColumn.confirmTitle'),
        text: t('kanban.page.swal.deleteColumn.confirmText', { name: column.nome }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: t('kanban.page.swal.deleteColumn.confirmYes'),
        cancelButtonText: t('kanban.page.swal.deleteColumn.cancel')
      })

      if (result.isConfirmed) {
        try {
          console.log('🗑️ Deletando coluna via API:', columnId)
          await api.delete(`/colunas-kanban/${columnId}`)
          await loadColumns()
          
          await Swal.fire({
            title: t('kanban.page.swal.deleteColumn.deletedTitle'),
            text: t('kanban.page.swal.deleteColumn.deletedText'),
            icon: 'success'
          })
        } catch (error) {
          console.error('❌ Erro ao deletar coluna:', error)
          await Swal.fire({
            title: t('kanban.page.swal.deleteColumn.errorTitle'),
            text: t('kanban.page.swal.deleteColumn.errorText'),
            icon: 'error'
          })
        }
      }
    }
    
    const handleColumnCreated = () => {
      loadColumns()
    }
    
    const handleColumnUpdated = (updatedColumn) => {
      console.log('🔄 Coluna atualizada:', updatedColumn)
      // Atualizar a coluna na lista
      const index = columns.value.findIndex(col => col.id === updatedColumn.id)
      if (index !== -1) {
        columns.value[index] = { ...columns.value[index], ...updatedColumn }
      }
      showEditColumnModal.value = false
    }
    
    const handleCardCreated = () => {
      if (workspace.value?.tipo === 'TAREFAS') {
        loadTasks()
      } else {
        loadLeads()
      }
    }
    
    // Métodos para modais detalhados
    const handleCardDetail = (card) => {
      console.log('🎯 handleCardDetail chamado com card:', card)
      console.log('🎯 Tipo do workspace:', workspace.value?.tipo)
      if (workspace.value?.tipo === 'TAREFAS') {
        selectedTask.value = card
        showTaskDetailModal.value = true
        console.log('🎯 Abrindo modal de task')
      } else {
        if (!canEditCrmLead.value) {
          promptCrmLeadUpgrade()
          return
        }
        selectedLead.value = card
        showLeadDetailModal.value = true
        console.log('🎯 Abrindo modal de lead')
      }
    }
    
    const handleTaskDetail = (task) => {
      selectedTask.value = task
      showTaskDetailModal.value = true
    }
    
    const handleLeadDetail = (lead) => {
      if (!canEditCrmLead.value) {
        promptCrmLeadUpgrade()
        return
      }
      selectedLead.value = lead
      showLeadDetailModal.value = true
    }
    
    const handleTaskSave = async (taskData) => {
      try {
        const response = await api.put(`/tasks/${selectedTask.value.id}`, taskData)
        // Atualizar task na lista
        const index = tasks.value.findIndex(t => t.id === selectedTask.value.id)
        if (index !== -1) {
          tasks.value[index] = response.data
        }
        showTaskDetailModal.value = false
      } catch (error) {
        console.error('Erro ao salvar task:', error)
      }
    }
    
    const handleTaskUpdated = (updatedTask) => {
      console.log('🔄 Task atualizada automaticamente:', updatedTask)
      
      // Atualizar task na lista
      const index = tasks.value.findIndex(t => t.id === updatedTask.id)
      if (index !== -1) {
        // Atualização SUAVE sem re-criar o array
        Object.assign(tasks.value[index], updatedTask)
        console.log('✅ Task atualizada na lista de tasks')
      }
      
      // Atualizar selectedTask com os dados mais recentes
      if (selectedTask.value && selectedTask.value.id === updatedTask.id) {
        selectedTask.value = {
          ...selectedTask.value,
          ...updatedTask
        }
      }
    }
    
    const handleLeadSave = async (leadData) => {
      try {
        console.log('💾 Salvando lead:', selectedLead.value.id)
        const response = await api.put(`/leads/${selectedLead.value.id}`, leadData)
        
        console.log('✅ Lead salvo, resposta:', response.data)
        
        // Atualizar lead na lista
        const index = leads.value.findIndex(l => l.id === selectedLead.value.id)
        if (index !== -1) {
          // Manter anexos e agendamentos existentes ao atualizar
          const updatedLead = {
            ...leads.value[index],
            ...response.data
          }
          
          // Só atualizar se realmente houve mudanças
          const hasChanges = JSON.stringify(leads.value[index]) !== JSON.stringify(updatedLead)
          if (hasChanges) {
            leads.value[index] = updatedLead
            console.log('✅ Lead atualizado na lista no índice:', index)
            
            // Forçar atualização da view (importante para recalcular totais)
            leads.value = [...leads.value]
          }
        }
        
        showLeadDetailModal.value = false
        console.log('✅ Modal fechado, Kanban atualizado!')
      } catch (error) {
        console.error('❌ Erro ao salvar lead:', error)
      }
    }
    
    const handleLeadUpdated = (updatedLead) => {
      console.log('🔄 Lead atualizado automaticamente:', updatedLead)

      if (updatedLead?.unarchived && updatedLead?.id) {
        loadLeads()
        if (showArchivedLeadsModal.value) {
          carregarArquivados(arquivadosPage.value)
        }
      }
      
      // Atualizar lead na lista
      const index = leads.value.findIndex(l => l.id === updatedLead.id)
      if (index !== -1) {
        // Atualização SUAVE sem re-criar o array
        Object.assign(leads.value[index], updatedLead)
        console.log('✅ Lead atualizado na lista de leads')
      }
      
      // Atualizar selectedLead com os dados mais recentes
      if (selectedLead.value && selectedLead.value.id === updatedLead.id) {
        const updatedSelectedLead = {
          ...selectedLead.value,
          ...updatedLead
        }
        
        // Só atualizar se realmente houve mudanças
        const hasChanges = JSON.stringify(selectedLead.value) !== JSON.stringify(updatedSelectedLead)
        if (hasChanges) {
          selectedLead.value = updatedSelectedLead
        }
      }
    }
    
    const handleAppointmentConfirm = (appointment) => {
      console.log('Agendamento confirmado:', appointment)
      showSchedulerModal.value = false
    }

    const carregarArquivados = async (p = 0) => {
      if (!workspaceId.value) return
      loadingArquivados.value = true
      arquivadosPage.value = p
      try {
        const params = new URLSearchParams()
        params.set('page', String(p))
        params.set('size', String(ARCHIVED_LEADS_PAGE_SIZE))
        if (arquivadosSearch.value) params.set('q', arquivadosSearch.value)
        const res = await api.get(`/leads/workspace/${workspaceId.value}/arquivados?${params.toString()}`)
        arquivadosLeads.value = res.data?.arquivados || []
        arquivadosTotal.value = Number(res.data?.totalArquivados) || 0
        arquivadosTotalPages.value = Number(res.data?.totalPages) || 0
        if (arquivadosTotalPages.value > 0 && p >= arquivadosTotalPages.value) {
          await carregarArquivados(arquivadosTotalPages.value - 1)
        }
      } catch (e) {
        console.error('Erro ao listar arquivados:', e)
        arquivadosLeads.value = []
        arquivadosTotal.value = 0
        arquivadosTotalPages.value = 0
      } finally {
        loadingArquivados.value = false
      }
    }

    const abrirModalArquivados = async () => {
      showArchivedLeadsModal.value = true
      arquivadosSearch.value = ''
      await carregarArquivados(0)
    }

    const onArquivadosSearch = async (q) => {
      arquivadosSearch.value = q || ''
      await carregarArquivados(0)
    }

    const abrirLeadArquivado = (lead) => {
      if (!lead?.id) return
      selectedLead.value = lead
      showLeadDetailModal.value = true
    }

    const desarquivarLeadKanban = async (lead) => {
      if (!lead?.id) return
      arquivadosBusyId.value = lead.id
      const paginaAtual = arquivadosPage.value
      try {
        await api.put(`/leads/${lead.id}/desarquivar`)
        await loadLeads()
        const proxima =
          arquivadosLeads.value.length <= 1 && paginaAtual > 0 ? paginaAtual - 1 : paginaAtual
        await carregarArquivados(proxima)
      } catch (e) {
        await Swal.fire({
          icon: 'error',
          title: t('integracao.swal.error'),
          text: e.response?.data?.error || t('kanban.page.archivedLeadsModal.unarchiveError'),
        })
      } finally {
        arquivadosBusyId.value = null
      }
    }

    const handleOpenExistingLead = async ({ id, unarchived }) => {
      if (!id) return
      showLeadDetailModal.value = false
      selectedLead.value = null
      try {
        if (unarchived) {
          await loadLeads()
        }
        let lead = leads.value.find((l) => Number(l.id) === Number(id))
        if (!lead) {
          const res = await api.get(`/leads/${id}`)
          lead = res.data
        }
        if (lead) {
          selectedLead.value = lead
          showLeadDetailModal.value = true
        }
      } catch (e) {
        console.error('Erro ao abrir lead existente:', e)
        await loadLeads()
      }
    }
    
    // Métodos para adicionar task/lead via botão do header
    const handleAddTask = (columnId) => {
      console.log('Adicionar task na coluna:', columnId)
      showCreateCardModal.value = true
    }
    
    const handleAddLead = (columnId) => {
      console.log('Adicionar lead na coluna:', columnId)
      showCreateCardModal.value = true
    }
    
    // Função de teste manual para anexos
    const testarAnexosManual = async () => {
      console.log('🧪 TESTE MANUAL DE ANEXOS INICIADO')
      
      if (workspace.value?.tipo !== 'CRM') {
        console.log('❌ Teste só funciona para workspaces CRM')
        return
      }
      
      console.log('🧪 Leads disponíveis:', leads.value.length)
      leads.value.forEach(lead => {
        console.log(`  📋 Lead ${lead.id}: ${lead.nome}`)
      })
      
      if (leads.value.length === 0) {
        console.log('❌ Nenhum lead disponível para teste')
        return
      }
      
      const leadTeste = leads.value[0]
      console.log(`🧪 Testando anexos para lead ${leadTeste.id} (${leadTeste.nome})`)
      
      try {
        const token = localStorage.getItem('token')
        console.log('🧪 Token:', token ? 'presente' : 'ausente')
        
        const response = await api.get(`/anexos-lead/lead/${leadTeste.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        console.log('🧪 Resposta da API:', response)
        console.log('🧪 Status:', response.status)
        console.log('🧪 Dados:', response.data)
        console.log('🧪 Tipo dos dados:', typeof response.data)
        console.log('🧪 É array?', Array.isArray(response.data))
        console.log('🧪 Tamanho:', response.data?.length)
        
        if (response.data && response.data.length > 0) {
          console.log('✅ TESTE BEM-SUCEDIDO: Anexos encontrados!')
          response.data.forEach((anexo, index) => {
            console.log(`  📎 Anexo ${index + 1}:`, anexo)
          })
          
          // Adicionar anexos ao lead manualmente
          leadTeste.anexos = response.data
          console.log('✅ Anexos adicionados ao lead manualmente')
          
          // Forçar atualização da view
          leads.value = [...leads.value]
          console.log('✅ View atualizada')
        } else {
          console.log('⚠️ TESTE: Nenhum anexo encontrado para este lead')
        }
        
      } catch (error) {
        console.log('❌ ERRO NO TESTE:', error)
        if (error.response) {
          console.log('❌ Status do erro:', error.response.status)
          console.log('❌ Dados do erro:', error.response.data)
        }
      }
    }

    // Drag entre colunas gerenciado pelo SortableJS
    
    // === Reordenação de Colunas ===
    const onMoveColumn = (evt) => {
      if (!effectivePermissions.value.canMoveColumns) {
        return false
      }
      // Impedir mover a coluna padrão (BackLog)
      const draggingColumn = columns.value[evt.draggedContext.index]
      const targetColumn = columns.value[evt.draggedContext.futureIndex]

      if ((draggingColumn && isColumnLocked(draggingColumn.id)) || (targetColumn && isColumnLocked(targetColumn.id))) {
        return false
      }
      
      if (draggingColumn && draggingColumn.isPadrao) {
        console.log('⚠️ Coluna padrão não pode ser movida')
        return false // Cancela o movimento
      }
      
      // Impedir mover para a posição 0 (reservada para BackLog)
      if (evt.draggedContext.futureIndex === 0) {
        console.log('⚠️ Posição 0 é reservada para a coluna padrão')
        return false
      }
      
      return true // Permite o movimento
    }
    
    const onColumnDragEnd = async (evt) => {
      if (!effectivePermissions.value.canMoveColumns) {
        await loadColumns()
        return
      }
      try {
        console.log('🔄 Reordenando colunas...')
        
        // Criar array com IDs na nova ordem
        const columnIds = columns.value.map(col => col.id)
        
        console.log('Nova ordem:', columnIds)
        
        // Enviar para backend
        const response = await api.put('/colunas-kanban/reordenar', columnIds)
        
        console.log('✅ Colunas reordenadas com sucesso')
        
        // O WebSocket irá notificar todos os usuários
      } catch (error) {
        console.error('❌ Erro ao reordenar colunas:', error)
        Swal.fire({
          icon: 'error',
          title: t('kanban.page.swal.reorderError.title'),
          text: t('kanban.page.swal.reorderError.text'),
        })
        
        // Recarregar colunas em caso de erro
        await loadColumns()
      }
    }
    
    const closeWebSocket = () => {
      wsIntentionalClose = true
      wsConnectGeneration++
      stopHeartbeat()
      if (wsReconnectTimer) {
        clearTimeout(wsReconnectTimer)
        wsReconnectTimer = null
      }
      disposeWebSocket()
    }

    const resetKanbanState = () => {
      columns.value = []
      tasks.value = []
      leads.value = []
      orderMap.value = {}
      workspaceTags.value = []
      workspaceUsers.value = []
      workspaceMotivos.value = []
      selectedTask.value = null
      selectedLead.value = null
      selectedColumn.value = null
      showCreateColumnModal.value = false
      showEditColumnModal.value = false
      showCreateCardModal.value = false
      showTaskDetailModal.value = false
      showLeadDetailModal.value = false
      showSchedulerModal.value = false
      showTogglesPopover.value = false
      wsHasConnectedBefore = false
    }

    const applyWorkspacePreferences = () => {
      showDescription.value = loadShowDescription()
      showTagsExpanded.value = loadShowTagsExpanded()
      showUsers.value = loadShowUsers()
      showActivityControls.value = loadShowActivityControls()
      showSchedule.value = loadShowSchedule()
      showChecklist.value = loadShowChecklist()
      showAttachments.value = loadShowAttachments()
      showContactDetails.value = loadShowContactDetails()
      showBusinessInfo.value = loadShowBusinessInfo()
      showFinanceiro.value = loadShowFinanceiro()
      showTaskProgress.value = loadShowTaskProgress()
      showCreatedAt.value = loadShowCreatedAt()
      showUpdatedAt.value = loadShowUpdatedAt()
    }

    // Lifecycle
    // Listener para eventos globais (não WebSocket)
    const handleLeadMessagesRead = async (event) => {
      console.log('📖 [KanbanPage] Evento lead.messages.read recebido:', event.detail)
      console.log('🔄 [KanbanPage] Recarregando leads para atualizar borda...')
      await loadLeads()
      console.log('✅ [KanbanPage] Leads recarregados após marcar como lidas')
    }
    
    // Fechar popover ao clicar fora
    const handleClickOutside = (event) => {
      if (showTogglesPopover.value && !event.target.closest('.view-toggles-wrapper')) {
        showTogglesPopover.value = false
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshNotificationPermission()
        reloadWhatsappNotifyPrefs()
      }
    }
    
    registerWhatsAppKanbanNotifyProvider(() => ({
      userId: authStore.user?.id,
      leads: leads.value,
      workspaceTipo: workspace.value?.tipo,
      showLeadDetailModal: showLeadDetailModal.value,
      selectedLeadId: selectedLead.value?.id,
      t
    }))
    ensureWhatsAppNotifyListener()

    onMounted(async () => {
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('click', handleKanbanUserGesture, { once: true, capture: true })
      reloadWhatsappNotifyPrefs()
      setWhatsAppNotificationClickHandler((leadId) => {
        const lead = leads.value.find((l) => Number(l.id) === Number(leadId))
        if (lead) {
          handleLeadDetail(lead)
        }
      })
      await workspaceStore.loadWorkspace(workspaceId.value)
      await planoFeatures.loadForWorkspace(workspaceId.value)
      await loadKanbanPermissions()
      await loadPreviewOptionsVisibility()
      
      // Ativar anexos por padrão para CRM se não houver preferência salva
      const savedAttachments = localStorage.getItem(getAttachmentsStorageKey())
      if (savedAttachments === null && workspace.value?.tipo === 'CRM') {
        showAttachments.value = true
      }
      
      await loadColumns()
      await loadTasks()
      await loadLeads()
      await loadWorkspaceTags()
      await loadWorkspaceUsers()
      orderMap.value = loadOrderMap()
      // Conectar WS por último para já ter dados base
      connectWebSocket()
      
      // Adicionar listener para mensagens lidas
      window.addEventListener('lead.messages.read', handleLeadMessagesRead)
    })

    watch(
      () => authStore.user?.id,
      () => {
        reloadWhatsappNotifyPrefs()
      }
    )

    watch(
      () => route.params.id,
      async (newId, oldId) => {
        if (!newId || newId === oldId) return

        console.log('[KANBAN] 🔄 Workspace mudou:', oldId, '->', newId)

        wsConnected.value = false
        closeWebSocket()
        resetKanbanState()

        workspaceId.value = parseInt(newId)
        applyWorkspacePreferences()

        await workspaceStore.loadWorkspace(workspaceId.value)
        await loadKanbanPermissions()
        await loadPreviewOptionsVisibility()

        // Ativar anexos por padrão para CRM se não houver preferência salva
        const savedAttachments = localStorage.getItem(getAttachmentsStorageKey())
        if (savedAttachments === null && workspace.value?.tipo === 'CRM') {
          showAttachments.value = true
        }

        await loadColumns()
        await loadTasks()
        await loadLeads()
        await loadWorkspaceTags()
        await loadWorkspaceUsers()
        orderMap.value = loadOrderMap()
        wsIntentionalClose = false
        wsReconnectAttempts = 0
        connectWebSocket()
      }
    )
    
    // Remover listener ao desmontar
    const cleanup = () => {
      registerWhatsAppKanbanNotifyProvider(null)
      window.removeEventListener('lead.messages.read', handleLeadMessagesRead)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('click', handleKanbanUserGesture, { capture: true })
      setWhatsAppNotificationClickHandler(null)
      closeWebSocket()
    }
    
    // onBeforeUnmount equivalent
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', cleanup)
    }

    onBeforeUnmount(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', cleanup)
      }
      cleanup()
    })
    
    return {
      t,
      toggleWorkspaceSubmenuMobile,
      workspaceId,
      workspace,
      canEditCrmLead,
      wsConnected,
      columns,
      tasks,
      leads,
      searchText,
      workspaceTags,
      workspaceUsers,
      workspaceMotivos,
      showCreateColumnModal,
      showEditColumnModal,
      showCreateCardModal,
      showArchivedLeadsModal,
      arquivadosLeads,
      arquivadosTotal,
      arquivadosPage,
      arquivadosTotalPages,
      arquivadosSearch,
      arquivadosBusyId,
      loadingArquivados,
      ARCHIVED_LEADS_PAGE_SIZE,
      showTaskDetailModal,
      showLeadDetailModal,
      showSchedulerModal,
      showTogglesPopover,
      selectedTask,
      selectedLead,
      selectedColumn,
      selectedDate,
      showDescription,
      toggleDescription,
      showTagsExpanded,
      toggleTagsExpanded,
      showUsers,
      toggleUsers,
      showActivityControls,
      toggleActivityControls,
      showSchedule,
      toggleSchedule,
      showChecklist,
      toggleChecklist,
      showAttachments,
      toggleAttachments,
      showContactDetails,
      toggleContactDetails,
      showBusinessInfo,
      toggleBusinessInfo,
      WHATSAPP_DESKTOP_NOTIFY_UI_ENABLED,
      whatsappNotifySound,
      whatsappNotifyDesktop,
      notificationPermission,
      notificationPermissionHint,
      toggleWhatsappNotifySound,
      toggleWhatsappNotifyDesktop,
      showFinanceiro,
      toggleFinanceiro,
      showTaskProgress,
      toggleTaskProgress,
      isPreviewOptionVisible,
      canUseKanbanPreviewMenu,
      canShowPreviewToggle,
      effectivePermissions,
      isColumnLocked,
      showCreatedAt,
      toggleShowCreatedAt,
      showUpdatedAt,
      toggleShowUpdatedAt,
      getCardsByColumn,
      handleMoveCard,
      handleUpdateCard,
      handleDeleteCard,
      handleEditColumn,
      handleDeleteColumn,
      handleColumnCreated,
      handleColumnUpdated,
      handleCardCreated,
      handleCardDetail,
      handleTaskDetail,
      handleLeadDetail,
      handleTaskSave,
      handleTaskUpdated,
      handleLeadSave,
      handleLeadUpdated,
      handleOpenExistingLead,
      handleAppointmentConfirm,
      abrirModalArquivados,
      carregarArquivados,
      onArquivadosSearch,
      abrirLeadArquivado,
      desarquivarLeadKanban,
      handleAddTask,
      handleAddLead,
      handleReorderLocal,
      onMoveColumn,
      onColumnDragEnd,
      orderMap,
      testarAnexosManual
    }
  }
}
</script>

<style scoped>
.kanban-page {
  @apply h-full flex flex-col overflow-hidden;
  background-color: var(--bg-primary);
  padding: 0;
  overflow-y: hidden;
}

/* Header Minimalista Limpo */
.clean-header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
  padding: 6px 0; /* TAMANHO ORIGINAL */
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 20;
}

.header-content {
  @apply flex justify-between items-center;
  width: 100%;
  padding: 0 8px; /* TAMANHO ORIGINAL */
}

.header-left {
  @apply flex items-center gap-3;
}

.mobile-header-submenu-btn {
  display: none;
}

.workspace-title {
  @apply text-lg font-semibold; /* TAMANHO ORIGINAL */
  color: var(--text-primary);
  margin: 0;
}

.workspace-type-badge {
  @apply text-xs px-2 py-1 rounded; /* TAMANHO ORIGINAL */
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
}

.workspace-type-badge.tarefas {
  background: #e0e7ff;
  color: #6366f1;
  border-color: #c7d2fe;
}

.workspace-type-badge.crm {
  background: #d1fae5;
  color: #10b981;
  border-color: #a7f3d0;
}

.header-center {
  @apply flex-1 flex justify-center;
}

.view-toggles-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.toggle-popover-btn {
  @apply flex items-center gap-2 px-3 py-1.5 rounded-md transition-all duration-200;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.toggle-popover-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-hover);
}

.toggle-popover-btn.active {
  background: var(--info);
  color: white;
  border-color: var(--info);
}

.toggle-popover-label {
  font-size: 13px;
}

.kanban-search-input-wrapper {
  display: inline-flex;
  align-items: center;
}

.kanban-search-input {
  @apply text-sm;
  padding: 6px 10px;
  border-radius: 9999px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  color: var(--text-primary);
  min-width: 220px;
}

.kanban-search-input::placeholder {
  color: var(--text-muted);
  opacity: 0.7;
}

.kanban-search-input:focus {
  outline: none;
  border-color: var(--info);
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.4);
}

.toggles-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 280px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  overflow: hidden;
}

.popover-header {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.popover-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.popover-content {
  padding: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.toggle-item {
  @apply flex items-center justify-between gap-4;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 4px;
  transition: background 0.2s ease;
}

.toggle-item:hover {
  background: var(--bg-secondary);
}

.toggle-item-info {
  @apply flex flex-col gap-1;
  flex: 1;
}

.toggle-item-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.toggle-item-desc {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.3;
}

.toggle-item-permission-hint {
  display: block;
  margin-top: 4px;
  font-size: 10px;
  line-height: 1.35;
  color: var(--text-secondary);
}

.toggle-item-permission-hint--granted {
  color: #22c55e;
}

.toggle-item-permission-hint--default {
  color: #eab308;
}

.toggle-item-permission-hint--denied,
.toggle-item-permission-hint--unsupported {
  color: #f97316;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toggle-switch.active {
  background: var(--info);
  border-color: var(--info);
}

.toggle-switch-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch.active .toggle-switch-slider {
  transform: translateX(20px);
}

.view-toggles {
  @apply flex items-center gap-1;
  background: var(--bg-secondary);
  padding: 2px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.toggle-btn {
  @apply flex items-center justify-center w-6 h-6 rounded transition-all duration-200;
  color: var(--text-secondary);
  background: transparent;
  border: none;
}

.toggle-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.toggle-btn.active {
  background: #6366f1;
  color: white;
  box-shadow: 0 1px 3px rgba(99, 102, 241, 0.3);
}

/* Tooltip Elegante - Para Baixo */
.tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  border: 1px solid var(--border-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1000;
  margin-top: 8px;
}

.tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-bottom-color: var(--bg-primary);
}

.tooltip-container:hover .tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(2px);
}

.header-right {
  @apply flex gap-2;
}

.action-btn {
  @apply flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold rounded-lg transition-all duration-300;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid transparent;
  min-width: 80px;
  height: 28px;
}

.action-btn--icon-only {
  min-width: 28px;
  width: 28px;
  padding-left: 0;
  padding-right: 0;
  justify-content: center;
  gap: 0;
}

/* Efeito shimmer nos botões */
.action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover::before {
  left: 100%;
}

.btn-icon {
  @apply flex items-center justify-center w-3 h-3 rounded-full;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.btn-text {
  font-weight: 600;
  letter-spacing: 0.2px;
  font-size: 11px;
  white-space: nowrap;
}

/* Botão Secundário Elegante */
.action-btn.secondary {
  background: linear-gradient(135deg, var(--bg-secondary) 0%, rgba(255, 255, 255, 0.05) 100%);
  color: var(--text-primary);
  border-color: var(--border-primary);
}

.action-btn.secondary:hover {
  background: linear-gradient(135deg, var(--bg-tertiary) 0%, rgba(107, 114, 128, 0.1) 100%);
  border-color: rgba(107, 114, 128, 0.3);
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 20px rgba(107, 114, 128, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.secondary:hover .btn-icon {
  background: rgba(107, 114, 128, 0.2);
  transform: scale(1.1);
}

/* Botão Primário Elegante */
.action-btn.primary {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #4f46e5 100%);
  color: white;
  border-color: #6366f1;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3), 0 2px 8px rgba(99, 102, 241, 0.2);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.action-btn.primary:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #4338ca 100%);
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 6px 25px rgba(99, 102, 241, 0.4), 0 4px 12px rgba(99, 102, 241, 0.3);
}

.action-btn.primary:hover .btn-icon {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1) rotate(90deg);
}

.action-btn.primary .btn-icon {
  background: rgba(255, 255, 255, 0.2);
}

/* Tema Claro */
.tema_clara .workspace-type-badge.tarefas {
  background: #e0e7ff;
  color: #6366f1;
  border-color: #c7d2fe;
}

.tema_clara .workspace-type-badge.crm {
  background: #d1fae5;
  color: #10b981;
  border-color: #a7f3d0;
}

.tema_clara .view-toggles {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.tema_clara .toggle-btn:hover {
  background: #e2e8f0;
}

.tema_clara .action-btn.secondary {
  background: linear-gradient(135deg, #f1f5f9 0%, rgba(255, 255, 255, 0.8) 100%);
  border-color: #e2e8f0;
}

.tema_clara .action-btn.secondary:hover {
  background: linear-gradient(135deg, #e2e8f0 0%, rgba(107, 114, 128, 0.05) 100%);
  border-color: #cbd5e1;
  box-shadow: 0 4px 20px rgba(107, 114, 128, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tema_clara .action-btn.secondary .btn-icon {
  background: rgba(107, 114, 128, 0.1);
}

.tema_clara .action-btn.secondary:hover .btn-icon {
  background: rgba(107, 114, 128, 0.2);
}

.tema_clara .tooltip {
  background: #1f2937;
  color: #f9fafb;
  border-color: #374151;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.tema_clara .tooltip::after {
  border-bottom-color: #1f2937;
}

/* Tema Escuro */
.tema_escura .workspace-type-badge.tarefas {
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  border-color: rgba(99, 102, 241, 0.3);
}

.tema_escura .workspace-type-badge.crm {
  background: rgba(16, 185, 129, 0.2);
  color: #6ee7b7;
  border-color: rgba(16, 185, 129, 0.3);
}

.tema_escura .view-toggles {
  background: #334155;
  border-color: #475569;
}

.tema_escura .toggle-btn:hover {
  background: #475569;
}

.tema_escura .action-btn.secondary {
  background: linear-gradient(135deg, #334155 0%, rgba(255, 255, 255, 0.05) 100%);
  border-color: #475569;
}

.tema_escura .action-btn.secondary:hover {
  background: linear-gradient(135deg, #475569 0%, rgba(107, 114, 128, 0.1) 100%);
  border-color: #64748b;
  box-shadow: 0 4px 20px rgba(107, 114, 128, 0.2), 0 2px 8px rgba(0, 0, 0, 0.2);
}

.tema_escura .action-btn.secondary .btn-icon {
  background: rgba(255, 255, 255, 0.1);
}

.tema_escura .action-btn.secondary:hover .btn-icon {
  background: rgba(255, 255, 255, 0.2);
}

.tema_escura .tooltip {
  background: #f9fafb;
  color: #1f2937;
  border-color: #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.tema_escura .tooltip::after {
  border-bottom-color: #f9fafb;
}

.kanban-board {
  @apply flex-1 overflow-x-auto;
  padding: 4px 8px 0 8px;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
}

.kanban-columns {
  @apply flex;
  gap: 0.3rem;
  flex: 1;
  height: 100%;
  padding-bottom: 8px;
  min-width: max-content;
}

.kanban-empty {
  @apply flex items-center justify-center min-h-[500px];
}

.empty-state {
  @apply text-center;
}

.empty-state svg {
  @apply w-16 h-16 mx-auto mb-4;
  color: var(--text-secondary);
}

.empty-state h3 {
  @apply text-lg font-semibold mb-2;
  color: var(--text-primary);
}

.empty-state p {
  @apply mb-4;
  color: var(--text-secondary);
}

/* Scrollbar personalizada */
.kanban-board::-webkit-scrollbar {
  height: 12px;
}

.kanban-board::-webkit-scrollbar-track {
  @apply rounded;
  background-color: var(--bg-tertiary);
  border-radius: 6px;
}

.kanban-board::-webkit-scrollbar-thumb {
  @apply rounded;
  background-color: var(--border-hover);
  border-radius: 6px;
  border: 2px solid var(--bg-tertiary);
}

.kanban-board::-webkit-scrollbar-thumb:hover {
  background-color: var(--text-primary);
}

/* Estilos para Drag & Drop de Colunas */
.column-ghost {
  opacity: 0.4;
  background: rgba(99, 102, 241, 0.1);
  border: 2px dashed #6366f1;
}

.column-chosen {
  opacity: 1;
  transform: scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  cursor: grabbing !important;
}

.column-dragging {
  opacity: 0.8;
  transform: rotate(2deg);
  transition: none !important;
}

.kanban-columns {
  user-select: none;
}

/* WebSocket reconnection indicator */
.ws-reconnecting-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: linear-gradient(90deg, #f59e0b, #d97706);
  padding: 6px 0;
  text-align: center;
}

.ws-reconnecting-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.ws-spinner {
  width: 16px;
  height: 16px;
  animation: ws-spin 1s linear infinite;
}

@keyframes ws-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.ws-fade-enter-active,
.ws-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.ws-fade-enter-from,
.ws-fade-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

@media (max-width: 768px) {
  .clean-header {
    padding: 8px 0;
  }

  .header-content {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
    padding: 0 8px;
  }

  .header-left {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .mobile-header-submenu-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid var(--border-primary);
    background: var(--bg-secondary);
    color: var(--text-primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  }

  .workspace-title {
    font-size: 15px;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: calc(100vw - 110px);
  }

  .workspace-type-badge {
    flex-shrink: 0;
    font-size: 10px;
    padding: 3px 6px;
  }

  .header-center,
  .header-right {
    width: 100%;
    flex: 1 1 100%;
  }

  .header-center {
    display: block;
  }

  .view-toggles-wrapper {
    width: 100%;
    display: flex;
    align-items: stretch;
    gap: 8px;
  }

  .toggle-popover-btn {
    flex: 0 0 auto;
    min-height: 38px;
    padding: 0 10px;
  }

  .toggle-popover-label {
    font-size: 12px;
  }

  .kanban-search-input-wrapper {
    flex: 1 1 auto;
    min-width: 0;
  }

  .kanban-search-input {
    width: 100%;
    min-width: 0;
    height: 38px;
  }

  .toggles-popover {
    position: fixed;
    top: 132px;
    left: 8px;
    right: 8px;
    min-width: 0;
    width: auto;
    max-height: 70vh;
    z-index: 1200;
  }

  .popover-content {
    max-height: calc(70vh - 48px);
  }

  .header-right {
    display: flex;
    gap: 8px;
  }

  .action-btn {
    min-width: 0;
    width: 100%;
    height: 36px;
    justify-content: center;
    padding: 0 10px;
  }

  .btn-text {
    font-size: 11px;
  }

  .kanban-board {
    padding: 6px 8px 0 8px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }

  .kanban-board.kanban-board--lock-horizontal,
  body.kanban-mobile-card-drag .kanban-board {
    overflow-x: hidden !important;
    scroll-snap-type: none !important;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .kanban-board.kanban-board--lock-horizontal::-webkit-scrollbar,
  body.kanban-mobile-card-drag .kanban-board::-webkit-scrollbar {
    display: none;
    height: 0;
    width: 0;
  }

  .kanban-columns {
    gap: 8px;
    padding-bottom: 10px;
  }
}
</style>


